/**
 * PurchaseInvoice Component (Refactored)
 * Alış Faturası - Hook'lar ve component'lere ayrılmış versiyon
 * 
 * Hook'lar:
 * - usePurchaseInvoiceForm: Form state ve handler'lar
 * - usePurchaseInvoiceData: Veri yükleme (cari, ürün, para birimi)
 * - usePurchaseInvoiceSave: Fatura kaydetme (atomik)
 * - usePurchaseInvoiceDraft: Taslak yönetimi
 * - usePurchaseInvoiceCalculations: Toplam hesaplamaları
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TablePagination,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import DraftsIcon from '@mui/icons-material/Drafts';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useAuth } from '../../../contexts/AuthContext';
import { getSellerByCompany, type SellerSnapshot } from '../../../services/sellerInfoService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Menu from '@mui/material/Menu';
import { useInventoryData } from '../inventory/hooks/useInventoryData';
import type { WarehouseStockDto } from '../inventory/types/inventory.types';
import PdfInvoiceUploadModal from './PdfInvoiceUploadModal';
import EInvoiceImportModal from './EInvoiceImportModal';
import EInvoiceAccountMatchModal from './EInvoiceAccountMatchModal';
import { ACCOUNTING_API, EINVOICE_API } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getBranches as getBranchesAdapter, type Branch } from '../../../services/migration/branchDataService';

// ===== Hook'lar =====
import {
  usePurchaseInvoiceForm,
  usePurchaseInvoiceData,
  usePurchaseInvoiceSave,
  usePurchaseInvoiceDraft,
  usePurchaseInvoiceCalculations,
} from './hooks';

// ===== Modallar =====
import { InvoiceSuccessModal } from './components/modals/InvoiceSuccessModal';
import { CopyItemsFromDocumentModal } from './components/modals/CopyItemsFromDocumentModal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// ===== Types =====
import type { Account, InvoiceItem, TevkifatType } from './components/types/invoice.types';

// ===== PYS =====
import ProjectPickerButton from '../shared/ProjectPickerButton';
import ExpenseCategorySelect from '../cashbox/ExpenseCategorySelect';

// ===== Utils =====
import { recalculateLineItem, formatQuantityForDisplay, tevkifatTypes } from './utils/invoiceCalculations';
import { readInvoiceLot } from './utils/invoiceLot';
import { addScannedLine, packageHint } from './utils/scannedLine';
import BarcodeScanField, { type ScannedInvoiceItem } from './components/BarcodeScanField';
import { ORDER_STOCK_HANDOVER_MESSAGE, isOrderStockProcessed } from './utils/orderStockHandover';
import { InvoiceLotCells, InvoiceLotHeaders, useShowInvoiceLots } from './components/tables/InvoiceLotCells';
import { displayTextFor, isFieldEmpty, findEmptyRequiredFields } from './utils/invoiceItemFields';
import { InvoiceNumberField } from './components/shared/InvoiceNumberField';
import { useInvoiceAgentBridge } from './hooks/useInvoiceAgentBridge';
import { setFormDirty } from '../../../utils/formLeaveGuard';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';
import { formatAmount } from '../../../utils/numberFormat';

// ===== Styled Components =====
const InvoiceHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const InvoiceSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
}));

const TotalLine = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
  width: '300px',
}));

// ===== Props Interface - Teklif ve Sipariş modu desteği =====
interface PurchaseInvoiceProps {
  mode?: 'invoice' | 'offer' | 'order';
  /** Şube yöneticisi paneli: şube kilitli, yalnız şube depoları, route yerine onClose. */
  isManager?: boolean;
  managerBranchId?: string | null;
  /** Route dışı düzenleme (manager paneli). */
  invoiceId?: string | null;
  /** Verilirse geri/başarı sonrası route yerine çağrılır. */
  onClose?: () => void;
}

// ===== Main Component =====
const PurchaseInvoice: React.FC<PurchaseInvoiceProps> = ({
  mode = 'invoice',
  isManager = false,
  managerBranchId = null,
  invoiceId: invoiceIdProp = null,
  onClose,
}) => {
  const isOfferMode = mode === 'offer';
  const isOrderMode = mode === 'order';
  // Form basliklari belge turune gore: Fatura/Teklif/Siparis Bilgileri vb.
  const docNoun = isOfferMode
    ? { tr: 'Teklif', en: 'Offer' }
    : isOrderMode
      ? { tr: 'Sipariş', en: 'Order' }
      : { tr: 'Fatura', en: 'Invoice' };
  
  const navigate = useNavigate();
  const successNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSuccessRedirectPath = useCallback(() => {
    if (isOrderMode) return '/siparisler';
    if (isOfferMode) return '/teklifler';
    return '/fatura/alis';
  }, [isOrderMode, isOfferMode]);

  const clearSuccessNavTimer = useCallback(() => {
    if (successNavTimerRef.current) {
      clearTimeout(successNavTimerRef.current);
      successNavTimerRef.current = null;
    }
  }, []);

  const leaveForm = useCallback(() => {
    if (onClose) onClose();
    else navigate(getSuccessRedirectPath());
  }, [onClose, navigate, getSuccessRedirectPath]);

  const scheduleSuccessRedirect = useCallback(() => {
    clearSuccessNavTimer();
    successNavTimerRef.current = setTimeout(() => {
      successNavTimerRef.current = null;
      leaveForm();
    }, 1500);
  }, [clearSuccessNavTimer, leaveForm]);

  useEffect(() => () => clearSuccessNavTimer(), [clearSuccessNavTimer]);
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const unitLabelMap: Record<string, string> = {
    adet: 'piece',
    kg: 'kg',
    gr: 'g',
    lt: 'l',
    ml: 'ml',
    m: 'm',
    cm: 'cm',
    'm²': 'm²',
    'm³': 'm³',
    çift: 'pair',
    paket: 'pack',
    kutu: 'box',
    top: 'roll',
    rulo: 'roll',
  };
  const localizeUnit = (unit: string) => (isTr ? unit : (unitLabelMap[unit] || unit));
  const normalizeUnitInput = (input: string) => {
    if (isTr) return input;
    const normalized = input.trim().toLowerCase();
    const matched = Object.entries(unitLabelMap).find(([, enLabel]) => enLabel.toLowerCase() === normalized);
    return matched?.[0] || input;
  };
  const { id: urlIdParam } = useParams<{ id?: string }>();
  const urlInvoiceId = invoiceIdProp || urlIdParam;
  const location = useLocation();
  const { warehouses: allWarehouses } = useAuth();

  // ===== Şube =====
  // Manager: kendi şubesine kilitli. Admin: opsiyonel seçim (boş → merkez/şubesiz).
  const isInvoiceMode = mode === 'invoice';
  const [branchOptions, setBranchOptions] = useState<Branch[]>([]);
  const [invoiceBranchId, setInvoiceBranchId] = useState<string | null>(isManager ? managerBranchId : null);
  // Kâr-Zarar gider türü (yalnız fatura modunda); boş → stok/mal alımı (SMM)
  const [expenseCategoryId, setExpenseCategoryId] = useState<number | null>(null);
  const [expenseCategoryName, setExpenseCategoryName] = useState<string | null>(null);
  useEffect(() => {
    if (isManager) setInvoiceBranchId(managerBranchId);
  }, [isManager, managerBranchId]);
  useEffect(() => {
    if (!isInvoiceMode) return;
    getBranchesAdapter('')
      .then(setBranchOptions)
      .catch((err) => console.warn('[PurchaseInvoice] Şubeler yüklenemedi:', err));
  }, [isInvoiceMode]);

  // Depo.BranchesId SQL Id ya da Firebase Id taşıyabilir; ikisi de eşleşmeli.
  const branchAliases = useMemo(() => {
    if (!invoiceBranchId) return null;
    const aliases = new Set<string>([invoiceBranchId]);
    const b = branchOptions.find(x => x.id === invoiceBranchId || String(x.sqlId) === invoiceBranchId);
    if (b) {
      aliases.add(b.id);
      if (b.sqlId != null) aliases.add(String(b.sqlId));
    }
    return aliases;
  }, [invoiceBranchId, branchOptions]);

  const warehouses = useMemo(
    () => (isManager
      ? allWarehouses.filter(w => !!w.branchesId && !!branchAliases?.has(String(w.branchesId)))
      : allWarehouses),
    [isManager, allWarehouses, branchAliases]
  );
  // Şubenin deposu yoksa kalemler depo seçimsiz kaydedilir (stok hareketi oluşmaz).
  const hideWarehouseUi = isManager && warehouses.length === 0;
  
  // URL parametrelerini parse et
  const queryParams = new URLSearchParams(location.search);
  const isFromOffer = queryParams.get('fromOffer') === 'true';
  const isFromOrder = queryParams.get('fromOrder') === 'true';
  const isFromReturn = queryParams.get('fromReturn') === 'true';
  const ettnFromUrl = queryParams.get('ettn') || null;
  const incomingPurchaseOrderId = queryParams.get('incomingPurchaseOrderId') || null;

  // EDM gelen kutusundan içe aktarımda kullanılacak hazır UBL XML içeriği
  const [eInvoiceXmlContent, setEInvoiceXmlContent] = useState<string | null>(null);

  // ✅ İade (alış-iadesi) state'leri — gönderilmiş bir alış faturasından iade oluşturma
  const [eInvoiceTypeCode, setEInvoiceTypeCode] = useState<string | null>(null);
  const [lotErrorLine, setLotErrorLine] = useState<number | null>(null);
  const [lotWarnings, setLotWarnings] = useState<string[]>([]);
  const [returnedInvoiceId, setReturnedInvoiceId] = useState<string | null>(null);
  const [returnedInvoiceNumber, setReturnedInvoiceNumber] = useState<string | null>(null);
  const [returnedInvoiceDate, setReturnedInvoiceDate] = useState<string | null>(null);
  // Aktif e-fatura entegratörü (iade gönderiminde/persist'te kullanılır: "edm" | "finansbank")
  const [activeEInvoiceProvider, setActiveEInvoiceProvider] = useState<string | null>(null);

  // ✅ Kaynak belge takip state'leri
  const [sourceOrderId, setSourceOrderId] = useState<string | null>(null);
  const [sourceOrderNumber, setSourceOrderNumber] = useState<string | null>(null);
  const [orderStockHandedOver, setOrderStockHandedOver] = useState(false); // Sipariş aşamasında stok işlendiyse bilgi notu
  const [sourceOfferId, setSourceOfferId] = useState<string | null>(null);
  const [sourceOfferNumber, setSourceOfferNumber] = useState<string | null>(null);

  // ✅ PR3 (Invoice Seller Snapshot Refactor): Manuel alış faturası için satıcı snapshot.
  // Manuel ekranda branch yok → Companies kaynağı. Backend resolver authoritatif.
  const [seller, setSeller] = useState<SellerSnapshot | null>(null);
  
  // ===== Data Hook =====
  const dataHook = usePurchaseInvoiceData({
    editingInvoiceId: urlInvoiceId || null,
    isFromOffer,
    ettnFromUrl,
  });
  
  const {
    companyId,
    loading: dataLoading,
    setLoading,
    error,
    setError,
    accounts,
    filteredAccounts,
    accountSearchTerm,
    accountsPage,
    accountsRowsPerPage,
    handleAccountSearch,
    handleAccountsPageChange,
    handleAccountsRowsPerPageChange,
    itemsPage,
    itemsRowsPerPage,
    itemSearchTerm,
    handleItemSearch,
    handleItemsPageChange,
    handleItemsRowsPerPageChange,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    loadAccounts,
    loadCurrencies,
    generateInvoiceNumber,
    loadInvoiceForEditing,
    loadOfferData,
    // E-fatura
    eInvoiceUsername,
    setEInvoiceUsername,
    eInvoicePassword,
    setEInvoicePassword,
    eInvoiceSupplierInfo,
    setEInvoiceSupplierInfo,
    isAccountMatchModalOpen,
    setIsAccountMatchModalOpen,
    isEInvoiceModalOpen,
    setIsEInvoiceModalOpen,
    // Stok işleme modal
    isStockProcessModalOpen,
    setIsStockProcessModalOpen,
    stockProcessCurrentItem,
    setStockProcessCurrentItem,
    stockProcessTotalItems,
    setStockProcessTotalItems,
    stockProcessSuccess,
    setStockProcessSuccess,
  } = dataHook;
  
  // ===== Inventory Data (Server-side pagination) =====
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);
  
  const { 
    inventoryItems, 
    loading: inventoryLoading,
    totalCount,
    loadInventoryItemsPaged
  } = useInventoryData(companyId, selectedWarehouseId);
  
  // ===== Form Hook =====
  const formHook = usePurchaseInvoiceForm({ 
    companyId, 
    inventoryItems,
    warehouses,
    initialEditingInvoiceId: invoiceIdProp,
  });
  
  const {
    editingInvoiceId,
    setEditingInvoiceId,
    pageTitle,
    selectedAccount,
    setSelectedAccount,
    isAccountModalOpen,
    setIsAccountModalOpen,
    handleOpenAccountModal,
    handleCloseAccountModal,
    handleSelectAccount: formSelectAccount,
    invoiceDate,
    setInvoiceDate,
    dueDate,
    setDueDate,
    invoiceNumber,
    setInvoiceNumber,
    notes,
    setNotes,
    projectId,
    setProjectId,
    isItemModalOpen,
    setIsItemModalOpen,
    selectedItems,
    setSelectedItems,
    handleOpenItemModal,
    handleCloseItemModal,
    handleAddItem,
    handleAddManualItem,
    handleDisplayQuantityChange,
    handleQuantityCommitOnBlur,
    handlePriceTypeChange,
    handleItemDisplayChange,
    handleItemNumberCommit,
    discountMode,
    setDiscountMode,
    handleDiscountModeChange,
    handleRemoveItem,
    handleItemNameChange,
    handleUnitChange,
    handleWarehouseChangeForItem,
    getWarehousesForItem,
    currency,
    setCurrency,
    exchangeRate,
    setExchangeRate,
    availableCurrencies,
    setAvailableCurrencies,
    handleCurrencyChange,
    formatCurrency,
    getCurrencySymbol,
    showTevkifat,
    setShowTevkifat,
    tevkifatRate,
    setTevkifatRate,
    tevkifatType,
    setTevkifatType,
    handleTevkifatTypeChange,
    handleTevkifatRateChange,
    handleAddTevkifat,
    handleRemoveTevkifat,
    showStopaj,
    setShowStopaj,
    stopajRate,
    setStopajRate,
    handleStopajRateChange,
    handleAddStopaj,
    handleRemoveStopaj,
    showAdditionalMenu,
    anchorEl,
    handleToggleAdditionalMenu,
    handleCloseMenu,
    originalInvoiceTotal,
    setOriginalInvoiceTotal,
    isBulkWarehouseModalOpen,
    setIsBulkWarehouseModalOpen,
    bulkWarehouseId,
    setBulkWarehouseId,
    handleBulkWarehouseSelect,
    handleGoBack,
    unitOptions,
  } = formHook;
  
  // URL'den gelen invoice id'yi form hook'a set et
  useEffect(() => {
    if (urlInvoiceId) {
      setEditingInvoiceId(urlInvoiceId);
    }
  }, [urlInvoiceId, setEditingInvoiceId]);

  // ✅ PR3 (Invoice Seller Snapshot Refactor): Mount'ta satıcı snapshot çek.
  // Manuel alış faturasında ekran/şube yok → Companies kaynağı. Backend resolver authoritatif.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getSellerByCompany()
      .then((snapshot) => {
        if (cancelled) return;
        setSeller(snapshot);
        console.log(
          '[PurchaseInvoice] 🏪 Seller snapshot:',
          `${snapshot.sellerCompanyName} (${snapshot.source}, franchise=${snapshot.isFranchiseInvoice})`,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[PurchaseInvoice] Seller fetch hatası:', err);
        setSeller(null);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // ===== Başka Belgeden Kalem Aktar =====
  const [isCopyFromDocumentModalOpen, setIsCopyFromDocumentModalOpen] = useState(false);

  const handleImportItemsFromDocument = (
    items: InvoiceItem[],
    strategy: 'append' | 'replace'
  ) => {
    if (strategy === 'replace') {
      setSelectedItems(items);
    } else {
      setSelectedItems(prev => [...prev, ...items]);
    }
  };
  
  // ===== Calculations Hook =====
  const calculationsHook = usePurchaseInvoiceCalculations({
    selectedItems,
    tevkifatRate,
    stopajRate,
    originalInvoiceTotal,
  });
  
  const {
    subtotal,
    netSubtotal,
    discountNetTotal,
    vatTotal,
    sctTotal,
    grandTotal,
    tevkifatAmount,
    stopajAmount,
    vatRateTotals,
    invoiceDifference,
    finalGrandTotal,
  } = calculationsHook;
  
  // ===== Draft Hook =====
  const draftHook = usePurchaseInvoiceDraft({
    companyId,
    editingInvoiceId,
    mode,
  });
  
  const {
    isDraftModalOpen,
    setIsDraftModalOpen,
    draftData,
    setDraftData,
    showDraftSavedAlert,
    saveDraftToLocalStorage,
    clearDraftFromLocalStorage,
    applyDraftToForm,
  } = draftHook;
  
  // ===== Save Hook =====
  const saveHook = usePurchaseInvoiceSave({
    companyId,
    editingInvoiceId,
    ettnFromUrl,
    selectedAccount,
    selectedItems,
    invoiceNumber,
    invoiceDate,
    dueDate,
    notes,
    projectId,
    expenseCategoryId,
    branchId: invoiceBranchId,
    currency,
    exchangeRate,
    tevkifatRate,
    tevkifatType,
    stopajRate,
    discountMode,
    grandTotal,
    originalInvoiceTotal,
    invoiceDifference,
    eInvoiceSupplierInfo,
    // ✅ İade (alış-iadesi) bilgileri
    eInvoiceTypeCode,
    returnedInvoiceId,
    returnedInvoiceNumber,
    returnedInvoiceDate,
    eInvoiceProvider: activeEInvoiceProvider,
    // ✅ Kaynak belge ilişkileri
    sourceOrderId,
    sourceOrderNumber,
    sourceOfferId,
    sourceOfferNumber,
    incomingPurchaseOrderId,
    setLoading,
    setError,
    setInvoiceNumber,
    setIsStockProcessModalOpen,
    setStockProcessTotalItems,
    setStockProcessCurrentItem,
    setStockProcessSuccess,
    setIsSuccessModalOpen,
    clearDraftFromLocalStorage,
    setLotWarnings,
    onLotLine: setLotErrorLine,
  });
  
  const { handleSaveInvoice: originalHandleSaveInvoice } = saveHook;
  const showInvoiceLots = useShowInvoiceLots(selectedItems) && isInvoiceMode;
  const purchaseLotMode = ['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(String(eInvoiceTypeCode || '').toUpperCase())
    ? 'purchaseReturn' as const
    : 'purchase' as const;
  const handleLotChange = (id: string, patch: Partial<InvoiceItem>) =>
    setSelectedItems(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
  const handleSplitLot = (id: string) => {
    setSelectedItems(prev => {
      const index = prev.findIndex(item => item.id === id);
      if (index < 0) return prev;
      const item = prev[index];
      const half = Math.round((item.quantity / 2) * 10000) / 10000;
      const rest = Math.round((item.quantity - half) * 10000) / 10000;
      if (!(rest > 0)) return prev;
      const kept = recalculateLineItem({ ...item, quantity: half, displayQuantity: formatQuantityForDisplay(half) });
      const copy = recalculateLineItem({
        ...item,
        id: `${id}_lot_${Date.now()}`,
        quantity: rest,
        displayQuantity: formatQuantityForDisplay(rest),
        lotNo: '',
      });
      const next = [...prev];
      next.splice(index, 1, kept, copy);
      return next;
    });
  };
  
  // ===== PDF Modal =====
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  // Kaydet denendi mi — boş bırakılan zorunlu sayısal alanları kırmızı göstermek için.
  const [attemptedSave, setAttemptedSave] = useState(false);
  
  // ===== TEKLİF MODU İÇİN STATE'LER =====
  const [validUntil, setValidUntil] = useState<string>('');
  const [offerStatus, setOfferStatus] = useState<'draft' | 'pending' | 'approved' | 'rejected'>('draft');
  
  // ===== SİPARİŞ MODU İÇİN STATE'LER =====
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [orderStatus, setOrderStatus] = useState<'draft' | 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'canceled'>('pending');
  const [processStock, setProcessStock] = useState<boolean>(false);
  const [showStockConfirmDialog, setShowStockConfirmDialog] = useState<boolean>(false);
  // Teklifte miktarı 0 olan kalemler için onay diyaloğu
  const [showZeroQtyConfirmDialog, setShowZeroQtyConfirmDialog] = useState<boolean>(false);
  const [zeroQtyItemNames, setZeroQtyItemNames] = useState<string[]>([]);
  
  // ===== DİNAMİK BAŞLIK VE NAVİGASYON =====
  const dynamicPageTitle = isOrderMode
    ? (editingInvoiceId ? tt("Alış Siparişi Düzenle", "Edit Purchase Order") : tt("Yeni Alış Siparişi", "New Purchase Order"))
    : isOfferMode 
      ? (editingInvoiceId ? tt("Alış Teklifi Düzenle", "Edit Purchase Offer") : tt("Yeni Alış Teklifi", "New Purchase Offer"))
      : pageTitle;
  
  const handleGoBackDynamic = () => {
    if (onClose) { onClose(); return; }
    navigate(isOrderMode ? '/siparisler' : isOfferMode ? '/teklifler' : '/fatura/alis');
  };
  
  // ===== Loading State =====
  const [loading, setLocalLoading] = useState(true);
  
  // ===== TEKLİF MODUNDA KAYDETME FONKSİYONU =====
  const handleSaveOffer = async (confirmZeroQty: boolean = false) => {
    if (!selectedAccount) { setError(tt('Lütfen cari ve en az bir ürün seçin.', 'Please select an account and at least one item.')); return; }
    if (selectedItems.length === 0) { setError('Lütfen en az bir ürün ekleyin.'); return; }
    // NOT: Tarih zorunluluğu KASITLI olarak kaldırıldı. Kullanıcı tarih girmese/yanlış girse bile
    // teklif kaydedilebilmeli. Backend boş/geçersiz tarihi toleranslı işler (bugüne düşer).

    // Miktarı 0 olan kalem(ler) varsa kullanıcıya sor; onaylarsa 0 miktarla da kaydedilir.
    const zeroQtyItems = selectedItems.filter((it) => !(Number(it.quantity) > 0));
    if (zeroQtyItems.length > 0 && confirmZeroQty !== true) {
      setZeroQtyItemNames(zeroQtyItems.map((it) => it.itemName || tt('(adsız ürün)', '(unnamed item)')));
      setShowZeroQtyConfirmDialog(true);
      return;
    }

    try {
      setLoading(true); setError(null);
      
      // Safari uyumlu tarih dönüştürme: Safari new Date() bazı formatları parse edemez ve
      // sessizce "NaN-NaN-NaN" üretir. Zaten YYYY-MM-DD ise dokunma; parse edilemezse null dön
      // (backend boş/geçersiz tarihi toleranslı işliyor, bugüne düşüyor).
      const formatDateOnly = (dateString: string) => {
        if (!dateString) return null;
        const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
        if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const offerDto = {
        companyId, 
        projectId,
        companyCode: 'DEFAULT', 
        offerType: 'PURCHASE_OFFER',
        accountId: selectedAccount.id.toString(), // Backend string bekliyor
        accountName: selectedAccount.companyName || null,
        accountTaxNumber: selectedAccount.taxNumber || null,
        accountTaxOffice: selectedAccount.taxOffice || null,
        accountAddress: selectedAccount.address || null,
        accountPhone: selectedAccount.phone || null,
        accountEmail: selectedAccount.email || null,
        offerDate: formatDateOnly(invoiceDate),
        validUntil: formatDateOnly(validUntil),
        status: offerStatus,
        currency, 
        exchangeRate: parseFloat(exchangeRate),
        notes: notes || null,
        description: `Alış Teklifi - ${selectedAccount.companyName}`, 
        createdBy: ownAuthSession.getCurrentEmail() || 'System',
        items: selectedItems.map((item, idx) => ({
          itemId: item.itemId.startsWith('manual_') ? null : item.itemId.toString(), // Backend string bekliyor
          itemName: item.itemName,
          isManualItem: item.itemId.startsWith('manual_'),
          quantity: item.quantity, 
          unit: item.unit, 
          unitPrice: item.unitPrice, 
          enteredPrice: item.enteredPrice,
          priceType: item.priceType || 'VAT_EXCLUDED',
          vatRate: item.vatRate, 
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0, 
          discountAmount: item.discountAmount || 0, 
          warehouseId: item.warehouseId ? item.warehouseId.toString() : null, // Backend string bekliyor
          lineNumber: idx + 1
        }))
      };
      
      const offerUrl = editingInvoiceId 
        ? ACCOUNTING_API.OFFER_UPDATE
        : ACCOUNTING_API.OFFER_CREATE;
      
      console.log('📤 [ALIŞ TEKLİFİ] Gönderilen DTO:', offerDto);
      const res = await accountingApi.post(offerUrl, editingInvoiceId ? { id: editingInvoiceId, ...offerDto } : offerDto);
      const result = res.data;
      console.log('📥 [ALIŞ TEKLİFİ] Backend yanıtı:', result);
      if (!result.success) throw new Error(result.message || 'Teklif kaydedilemedi');
      
      clearDraftFromLocalStorage(); 
      setIsSuccessModalOpen(true);
      scheduleSuccessRedirect();
    } catch (err: any) {
      // Sunucunun gerçek 400 mesajını göster (ModelState validasyon hataları dahil)
      const data = err?.response?.data;
      console.error('❌ [ALIŞ TEKLİFİ] Backend hata yanıtı:', data ?? err);
      let msg: string | undefined = data?.message ?? data?.Message;
      const modelErrors = data?.errors ?? data?.Errors;
      if (modelErrors && typeof modelErrors === 'object') {
        const details = Object.entries(modelErrors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join(' | ');
        msg = msg ? `${msg} — ${details}` : details;
      }
      setError(msg || err.message || 'Teklif kaydedilemedi');
    } finally { setLoading(false); }
  };
  
  // ✅ SİPARİŞ MODUNDA KAYDETME FONKSİYONU
  const handleSaveOrder = async (stockChoice?: boolean) => {
    if (!selectedAccount) { setError(tt('Lütfen cari seçin.', 'Please select an account.')); return; }
    if (selectedItems.length === 0) { setError('Lütfen en az bir ürün ekleyin.'); return; }
    
    // Stok onay dialogu - stockChoice undefined ise henüz seçim yapılmamış
    // Düzenleme modunda stok sorusu SORULMAZ, doğrudan kaydet
    if (stockChoice === undefined) {
      if (editingInvoiceId) {
        stockChoice = false;
      } else {
        setShowStockConfirmDialog(true);
        return;
      }
    }
    
    // stockChoice parametresini state'e de yansıt (UI tutarlılığı için)
    setProcessStock(stockChoice);
    
    try {
      setLoading(true); setError(null);
      
      const formatDateOnly = (dateString: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      const orderDto = {
        companyId, 
        projectId,
        companyName: 'DEFAULT', 
        orderType: 'PURCHASE_ORDER',
        accountId: selectedAccount.id.toString(),
        accountName: selectedAccount.companyName || null,
        accountTaxNumber: selectedAccount.taxNumber || null,
        accountTaxOffice: selectedAccount.taxOffice || null,
        accountAddress: selectedAccount.address || null,
        accountPhone: selectedAccount.phone || null,
        accountEmail: selectedAccount.email || null,
        orderDate: formatDateOnly(invoiceDate),
        deliveryDate: deliveryDate ? formatDateOnly(deliveryDate) : null,
        processStock: stockChoice,
        status: orderStatus,
        currency, 
        exchangeRate: parseFloat(exchangeRate),
        tevkifatRate: tevkifatRate !== "0" ? tevkifatRate : null,
        tevkifatType: tevkifatType || null,
        stopajRate: stopajRate || null,
        notes: notes || null,
        description: `Alış Siparişi - ${selectedAccount.companyName}`, 
        createdBy: ownAuthSession.getCurrentEmail() || 'System',
        // ✅ Teklif ilişkisi (tekliften sipariş oluşturuluyorsa)
        sourceOfferId: null,
        sourceOfferNumber: null,
        items: selectedItems.map((item, idx) => ({
          itemId: item.itemId.startsWith('manual_') ? null : item.itemId.toString(),
          itemName: item.itemName,
          isManualItem: item.itemId.startsWith('manual_'),
          quantity: item.quantity, 
          unit: item.unit, 
          unitPrice: item.unitPrice, 
          enteredPrice: item.enteredPrice,
          priceType: item.priceType || 'VAT_EXCLUDED',
          vatRate: item.vatRate, 
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0, 
          discountAmount: item.discountAmount || 0, 
          warehouseId: item.warehouseId ? item.warehouseId.toString() : null,
          lineNumber: idx + 1
        }))
      };
      
      const orderUrl = editingInvoiceId 
        ? ACCOUNTING_API.ORDER_UPDATE
        : ACCOUNTING_API.ORDER_CREATE;
      
      console.log('📤 [ALIŞ SİPARİŞİ] Gönderilen DTO:', orderDto);
      const res = await accountingApi.post(orderUrl, editingInvoiceId ? { id: editingInvoiceId, ...orderDto } : orderDto);
      const result = res.data;
      console.log('📥 [ALIŞ SİPARİŞİ] Backend yanıtı:', result);
      if (!result.success) throw new Error(result.message || 'Sipariş kaydedilemedi');
      
      clearDraftFromLocalStorage(); 
      setIsSuccessModalOpen(true);
      scheduleSuccessRedirect();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };
  
  // ✅ TEKLİF DÜZENLEME İÇİN VERİ YÜKLEME
  const loadOfferForEditing = async (offerId: string, currentCompanyId: string) => {
    try {
      console.log('📤 [ALIŞ TEKLİFİ] Teklif yükleniyor:', offerId);
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(offerId));
      const result = response.data;
      console.log('📥 [ALIŞ TEKLİFİ] Yükleme yanıtı:', result);
      
      if (!result.success) throw new Error('Teklif yüklenemedi');
      const sqlData = result.data;
      
      // Form alanlarını doldur
      setInvoiceNumber(sqlData.offerNumber || '');
      setInvoiceDate(sqlData.offerDate ? sqlData.offerDate.split('T')[0] : '');
      setValidUntil(sqlData.validUntil ? sqlData.validUntil.split('T')[0] : '');
      setOfferStatus(sqlData.status || 'pending');
      setNotes(sqlData.notes || '');
      setProjectId(sqlData.projectId ?? null);
      setCurrency(sqlData.currency || 'TRY');
      setExchangeRate(sqlData.exchangeRate?.toString() || '1');
      
      // Cari bilgilerini doldur
      if (sqlData.accountId) {
        const accountData: Account = {
          id: sqlData.accountId,
          companyName: sqlData.accountName || '',
          taxNumber: sqlData.accountTaxNumber || '',
          taxOffice: sqlData.accountTaxOffice || '',
          address: sqlData.accountAddress || '',
          phone: sqlData.accountPhone || '',
          email: sqlData.accountEmail || '',
          authorizedPerson: '',
          city: '',
          district: '',
        };
        setSelectedAccount(accountData);
      }
      
      // Kalem bilgilerini doldur - API camelCase veya PascalCase dönebilir
      const itemsData = sqlData.items || sqlData.Items || [];
      console.log('🔍 [DEBUG] PurchaseInvoice itemsData:', itemsData);
      const items = itemsData.map((item: any) => ({
        id: item.id || Date.now().toString(),
        itemId: item.itemId || `manual_${item.id}`,
        itemName: item.itemName,
        quantity: item.quantity,
        displayQuantity: formatQuantityForDisplay(item.quantity),
        unit: item.unit,
        priceType: item.priceType || 'VAT_EXCLUDED',
        enteredPrice: item.enteredPrice || item.unitPrice,
        unitPrice: item.unitPrice,
        unitPriceWithVat: item.unitPrice * (1 + (item.vatRate || 0) / 100),
        vatRate: item.vatRate || 0,
        sctRate: item.sctRate || 0,
        discountRate: item.discountRate || 0,
        discountAmount: item.discountAmount || 0,
        lineTotal: item.lineTotal,
        netTotal: item.netTotal,
        vatAmount: item.vatAmount,
        sctAmount: item.sctAmount || 0,
        grossTotal: item.grossTotal,
        warehouseId: item.warehouseId || undefined,
      }));
      setSelectedItems(items);
      
      // Tevkifat
      if (sqlData.tevkifatRate && sqlData.tevkifatRate !== "0") {
        setShowTevkifat(true);
        setTevkifatRate(sqlData.tevkifatRate);
        setTevkifatType(sqlData.tevkifatType || "");
      }
      
      // Stopaj
      if (sqlData.stopajRate && sqlData.stopajRate > 0) {
        setShowStopaj(true);
        setStopajRate(sqlData.stopajRate);
      }
      
      setLocalLoading(false);
      console.log('✅ [ALIŞ TEKLİFİ] Teklif başarıyla yüklendi');
    } catch (err) {
      console.error('❌ [ALIŞ TEKLİFİ] Teklif yüklenirken hata:', err);
      setError('Teklif bilgileri yüklenirken hata oluştu.');
      navigate('/teklifler');
      setLocalLoading(false);
    }
  };
  
  // ✅ SİPARİŞ DÜZENLEME İÇİN VERİ YÜKLEME
  const loadOrderForEditing = async (orderId: string, currentCompanyId: string) => {
    try {
      console.log('📤 [ALIŞ SİPARİŞİ] Sipariş yükleniyor:', orderId);
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(orderId));
      const result = response.data;
      console.log('📥 [ALIŞ SİPARİŞİ] Yükleme yanıtı:', result);
      
      if (!result.success) throw new Error('Sipariş yüklenemedi');
      const sqlData = result.data;
      
      setInvoiceNumber(sqlData.orderNumber || '');
      setInvoiceDate(sqlData.orderDate ? sqlData.orderDate.split('T')[0] : '');
      setDeliveryDate(sqlData.deliveryDate ? sqlData.deliveryDate.split('T')[0] : '');
      setOrderStatus(sqlData.status || 'pending');
      setProcessStock(sqlData.processStock || false);
      setNotes(sqlData.notes || '');
      setProjectId(sqlData.projectId ?? null);
      setCurrency(sqlData.currency || 'TRY');
      setExchangeRate(sqlData.exchangeRate?.toString() || '1');
      
      if (sqlData.accountId) {
        setSelectedAccount({
          id: sqlData.accountId,
          companyName: sqlData.accountName || '',
          taxNumber: sqlData.accountTaxNumber || '',
          taxOffice: sqlData.accountTaxOffice || '',
          address: sqlData.accountAddress || '',
          phone: sqlData.accountPhone || '',
          email: sqlData.accountEmail || '',
          authorizedPerson: '',
          city: '',
          district: '',
        } as any);
      }
      
      const itemsData = sqlData.items || sqlData.Items || [];
      const items = itemsData.map((item: any) => ({
        id: item.id || Date.now().toString(),
        itemId: item.itemId || `manual_${item.id}`,
        itemName: item.itemName,
        quantity: item.quantity,
        displayQuantity: formatQuantityForDisplay(item.quantity),
        unit: item.unit,
        priceType: item.priceType || 'VAT_EXCLUDED',
        enteredPrice: item.enteredPrice || item.unitPrice,
        unitPrice: item.unitPrice,
        unitPriceWithVat: item.unitPrice * (1 + (item.vatRate || 0) / 100),
        vatRate: item.vatRate || 0,
        sctRate: item.sctRate || 0,
        discountRate: item.discountRate || 0,
        discountAmount: item.discountAmount || 0,
        lineTotal: item.lineTotal,
        netTotal: item.netTotal,
        vatAmount: item.vatAmount,
        sctAmount: item.sctAmount || 0,
        grossTotal: item.grossTotal,
        warehouseId: item.warehouseId || undefined,
      }));
      setSelectedItems(items);
      
      if (sqlData.tevkifatRate && sqlData.tevkifatRate !== "0") {
        setShowTevkifat(true);
        setTevkifatRate(sqlData.tevkifatRate);
        setTevkifatType(sqlData.tevkifatType || "");
      }
      if (sqlData.stopajRate && sqlData.stopajRate > 0) {
        setShowStopaj(true);
        setStopajRate(sqlData.stopajRate);
      }
      
      setLocalLoading(false);
      console.log('✅ [ALIŞ SİPARİŞİ] Sipariş başarıyla yüklendi');
    } catch (err) {
      console.error('❌ [ALIŞ SİPARİŞİ] Sipariş yüklenirken hata:', err);
      setError('Sipariş bilgileri yüklenirken hata oluştu.');
      navigate('/siparisler');
      setLocalLoading(false);
    }
  };
  
  // ===== KAYDETME HANDLER'I (Mode'a göre) =====
  const delegateSaveInvoice = isOrderMode ? handleSaveOrder : isOfferMode ? handleSaveOffer : originalHandleSaveInvoice;

  // Boş bırakılan zorunlu sayısal alanlar üç kayıt yolunun hepsinde engellenir;
  // hangi kalemde hangi alanın eksik olduğu tek tek bildirilir.
  const handleSaveInvoice = (arg?: boolean) => {
    setAttemptedSave(true);
    const emptyFieldReports = findEmptyRequiredFields(selectedItems);
    if (emptyFieldReports.length > 0) {
      const fieldLabels: Record<string, string> = {
        quantity: tt('miktar', 'quantity'),
        price: tt('fiyat', 'price'),
        vatRate: tt('KDV oranı', 'VAT rate'),
      };
      const details = emptyFieldReports
        .map(r => `${r.index}. ${tt('kalem', 'item')} (${r.itemName || tt('adsız ürün', 'unnamed item')}): ${r.fields.map(f => fieldLabels[f]).join(', ')}`)
        .join(' • ');
      setError(`${tt('Boş bırakılan alanlar var. Lütfen doldurun:', 'There are empty fields. Please fill them in:')} ${details}`);
      return;
    }
    return delegateSaveInvoice(arg as boolean);
  };

  // ✅ AI ajanı köprüsü: fatura + alış siparişi + alış teklifi.
  useInvoiceAgentBridge({
    formType: isOrderMode ? 'orderPurchase' : isOfferMode ? 'offerPurchase' : 'purchase',
    enabled: mode === 'invoice' || mode === 'order' || mode === 'offer',
    selectAccount: (acc) => formSelectAccount(acc),
    setField: (field, value) => {
      switch (field) {
        case 'invoiceDate': setInvoiceDate(String(value)); return true;
        case 'dueDate': setDueDate(String(value)); return true;
        case 'invoiceNumber': setInvoiceNumber(String(value)); return true;
        case 'notes': setNotes(String(value)); return true;
        case 'currency': setCurrency(String(value).toUpperCase()); return true;
        case 'exchangeRate': setExchangeRate(String(value)); return true;
        case 'deliveryDate':
          if (!isOrderMode) return false;
          setDeliveryDate(String(value));
          return true;
        case 'processStock':
          if (!isOrderMode) return false;
          setProcessStock(value === true || value === 'true');
          return true;
        case 'orderStatus': {
          if (!isOrderMode) return false;
          const st = String(value).toLowerCase();
          if (['draft', 'pending', 'confirmed', 'shipped', 'delivered', 'canceled'].includes(st)) {
            setOrderStatus(st as any);
            return true;
          }
          return false;
        }
        case 'validUntil':
          if (!isOfferMode) return false;
          setValidUntil(String(value));
          return true;
        case 'offerStatus': {
          if (!isOfferMode) return false;
          const st = String(value).toLowerCase();
          if (['draft', 'pending', 'approved', 'rejected'].includes(st)) {
            setOfferStatus(st as any);
            return true;
          }
          return false;
        }
        case 'tevkifatType': {
          const code = String(value);
          setShowTevkifat(true); setTevkifatType(code);
          const t = tevkifatTypes.find(x => x.code === code);
          if (t) setTevkifatRate(t.rate);
          return true;
        }
        case 'stopajRate': setShowStopaj(true); setStopajRate(Number(value) || 0); return true;
        default: return false; // paymentMethod vb. alış formunda yok
      }
    },
    setItems: (updater) => setSelectedItems(prev => updater(prev)),
    getDefaultWarehouseId: () =>
      selectedWarehouseId || warehouses.find(w => w.isDefault)?.id || warehouses[0]?.id,
    getState: () => ({
      accountName: selectedAccount?.companyName ?? null,
      invoiceNumber, invoiceDate, dueDate, currency,
      items: selectedItems,
      subtotal, vatTotal, grandTotal,
      error,
    }),
    save: async () => { await handleSaveInvoice(); },
  });

  // ===== Initial Data Loading =====
  useEffect(() => {
    const loadInitialData = async () => {
      if (!companyId) return;
      
      setLocalLoading(true);
      setError(null);
      
      try {
        // Cari hesapları yükle
        await loadAccounts(companyId);
        
        // Para birimlerini yükle
        const currencies = await loadCurrencies(companyId);
        setAvailableCurrencies(currencies);
        
        // Düzenleme modu
        if (editingInvoiceId) {
          // ✅ Offer/Order mode'da teklif/sipariş, fatura mode'da fatura yükle
          if (isOfferMode) {
            await loadOfferForEditing(editingInvoiceId, companyId);
          } else if (isOrderMode) {
            await loadOrderForEditing(editingInvoiceId, companyId);
          } else {
            const result = await loadInvoiceForEditing(
              editingInvoiceId,
              companyId,
              onClose ? () => onClose() : navigate
            );
            if (result.invoice) {
              if (!isManager) setInvoiceBranchId(result.invoice.branchId || null);
              setInvoiceNumber(result.invoice.invoiceNumber);
              setInvoiceDate(result.invoice.date);
              setDueDate(result.invoice.dueDate || '');
              setNotes(result.invoice.notes || '');
              setProjectId(result.invoice.projectId ?? null);
              setExpenseCategoryId(result.invoice.expenseCategoryId ?? null);
              setExpenseCategoryName(result.invoice.expenseCategoryName ?? null);
              setCurrency(result.invoice.currency || 'TRY');
              setExchangeRate(result.invoice.exchangeRate?.toString() || '1');
              setDiscountMode(result.invoice.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT');
              setSelectedItems(result.items);
              
              if (result.account) {
                setSelectedAccount(result.account);
              }
              
              // Tevkifat/Stopaj
              if (result.invoice.tevkifatRate && result.invoice.tevkifatRate !== "0") {
                setShowTevkifat(true);
                setTevkifatRate(result.invoice.tevkifatRate);
                setTevkifatType(result.invoice.tevkifatType || "");
              }
              
              if (result.invoice.stopajRate && result.invoice.stopajRate > 0) {
                setShowStopaj(true);
                setStopajRate(result.invoice.stopajRate);
              }
              
              // Orijinal fatura tutarı
              if (result.invoice.originalInvoiceTotal) {
                const formattedValue = new Intl.NumberFormat('tr-TR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(result.invoice.originalInvoiceTotal);
                setOriginalInvoiceTotal(formattedValue);
              }
            }
          }
        } else {
          // Yeni fatura - numara oluştur
          const newInvoiceNumber = await generateInvoiceNumber(companyId);
          setInvoiceNumber(newInvoiceNumber);
          
          // ✅ Tekliften sipariş oluşturma (order mode + fromOffer)
          if (isFromOffer && isOrderMode) {
            const orderDataStr = localStorage.getItem('offerToOrder');
            if (orderDataStr) {
              try {
                const offerData = JSON.parse(orderDataStr);
                localStorage.removeItem('offerToOrder');
                console.log('📥 [OFFER→ORDER] Teklif verisi yüklendi (alış sipariş):', offerData);
                
                // Cari hesabı ayarla
                if (offerData.supplierId || offerData.accountId) {
                  const accountData: Account = {
                    id: (offerData.supplierId || offerData.accountId || '').toString(),
                    companyName: offerData.supplier?.companyName || offerData.account?.companyName || '',
                    authorizedPerson: '',
                    taxNumber: offerData.supplier?.taxNumber || offerData.account?.taxNumber || '',
                    taxOffice: offerData.supplier?.taxOffice || offerData.account?.taxOffice || '',
                    address: offerData.supplier?.address || offerData.account?.address || '',
                    city: '',
                    district: '',
                    phone: offerData.supplier?.phone || offerData.account?.phone || '',
                    email: offerData.supplier?.email || offerData.account?.email || '',
                  };
                  setSelectedAccount(accountData);
                }
                
                // Ürünleri dönüştür
                if (offerData.items && offerData.items.length > 0) {
                  const defaultWarehouse = warehouses.find(w => w.isDefault);
                  const items: InvoiceItem[] = offerData.items.map((item: any) => {
                    const quantity = item.quantity || 0;
                    const unitPrice = item.unitPrice || 0;
                    const vatRate = item.vatRate || 0;
                    const sctRate = item.sctRate || 0;
                    const discountRate = item.discountRate || 0;
                    const lineTotal = quantity * unitPrice;
                    const discountAmount = lineTotal * (discountRate / 100);
                    const netTotal = lineTotal - discountAmount;
                    const vatAmount = netTotal * (vatRate / 100);
                    const sctAmount = netTotal * (sctRate / 100);
                    const grossTotal = netTotal + vatAmount + sctAmount;
                    
                    return {
                      id: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                      itemId: item.itemId || item.stockId || 'manual_' + Date.now(),
                      itemName: item.itemName || item.name || '',
                      quantity,
                      unit: item.unit || 'adet',
                      enteredPrice: unitPrice,
                      priceType: 'VAT_EXCLUDED' as const,
                      unitPrice,
                      unitPriceWithVat: unitPrice * (1 + vatRate / 100),
                      vatRate, sctRate, discountRate, discountAmount,
                      lineTotal, netTotal, vatAmount, sctAmount, grossTotal,
                      warehouseId: item.warehouseId || defaultWarehouse?.id || '',
                      displayQuantity: formatQuantityForDisplay(quantity)
                    };
                  });
                  setSelectedItems(items);
                }
                
                if (offerData.notes) setNotes(offerData.notes);
                if (offerData.currency) setCurrency(offerData.currency);
                // PYS: proje bağı belge zincirinde taşınır (teklif → sipariş)
                setProjectId(offerData.projectId ?? null);
              } catch (err) {
                console.error('❌ [OFFER→ORDER] Teklif verisi parse hatası:', err);
              }
            }
          }
          // ✅ Tekliften fatura oluşturma
          else if (isFromOffer) {
            const offerResult = await loadOfferData();
            if (offerResult.account) {
              setSelectedAccount(offerResult.account);
            }
            if (offerResult.items.length > 0) {
              setSelectedItems(offerResult.items);
            }
          }
          
          // ✅ Siparişten fatura oluşturma
          if (isFromOrder) {
            const orderDataStr = localStorage.getItem('orderToInvoice');
            if (orderDataStr) {
              try {
                const orderData = JSON.parse(orderDataStr);
                localStorage.removeItem('orderToInvoice');
                console.log('📥 [ORDER→INVOICE] Sipariş verisi yüklendi (alış faturası):', orderData);
                
                // Cari hesabı ayarla
                if (orderData.accountId) {
                  const accountData: Account = {
                    id: orderData.accountId.toString(),
                    companyName: orderData.account?.companyName || '',
                    authorizedPerson: '',
                    taxNumber: orderData.account?.taxNumber || '',
                    taxOffice: orderData.account?.taxOffice || '',
                    address: orderData.account?.address || '',
                    city: '',
                    district: '',
                    phone: orderData.account?.phone || '',
                    email: orderData.account?.email || '',
                  };
                  setSelectedAccount(accountData);
                }
                
                // Ürünleri dönüştür
                if (orderData.items && orderData.items.length > 0) {
                  const defaultWarehouse = warehouses.find(w => w.isDefault);
                  const items: InvoiceItem[] = orderData.items.map((item: any) => {
                    const quantity = item.quantity || 0;
                    const unitPrice = item.unitPrice || 0;
                    const vatRate = item.vatRate || 0;
                    const sctRate = item.sctRate || 0;
                    const discountRate = item.discountRate || 0;
                    const lineTotal = quantity * unitPrice;
                    const discountAmount = lineTotal * (discountRate / 100);
                    const netTotal = lineTotal - discountAmount;
                    const vatAmount = netTotal * (vatRate / 100);
                    const sctAmount = netTotal * (sctRate / 100);
                    const grossTotal = netTotal + vatAmount + sctAmount;
                    
                    return {
                      id: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                      itemId: item.itemId || item.stockId || 'manual_' + Date.now(),
                      itemName: item.itemName || item.name || '',
                      quantity,
                      unit: item.unit || 'adet',
                      enteredPrice: unitPrice,
                      priceType: (item.priceType || 'VAT_EXCLUDED') as 'VAT_EXCLUDED' | 'VAT_INCLUDED',
                      unitPrice,
                      unitPriceWithVat: unitPrice * (1 + vatRate / 100),
                      vatRate, sctRate, discountRate, discountAmount,
                      lineTotal, netTotal, vatAmount, sctAmount, grossTotal,
                      warehouseId: item.warehouseId || defaultWarehouse?.id || '',
                      displayQuantity: formatQuantityForDisplay(quantity)
                    };
                  });
                  setSelectedItems(items);
                }
                
                if (orderData.notes) setNotes(orderData.notes);
                if (orderData.currency) setCurrency(orderData.currency);
                // PYS: proje bağı belge zincirinde taşınır (sipariş → fatura)
                setProjectId(orderData.projectId ?? null);

                // ✅ Kaynak sipariş takibi
                if (orderData.orderId) setSourceOrderId(orderData.orderId);
                if (orderData.orderNumber) setSourceOrderNumber(orderData.orderNumber);
                setOrderStockHandedOver(isOrderStockProcessed(orderData));
              } catch (err) {
                console.error('❌ [ORDER→INVOICE] Sipariş verisi parse hatası:', err);
              }
            }
          }
        }
      } catch (err: any) {
        setError(err.message || 'Veri yüklenirken hata oluştu');
      } finally {
        setLocalLoading(false);
      }
    };
    
    loadInitialData();
  }, [companyId, editingInvoiceId, isFromOffer, isFromOrder, isOfferMode, isOrderMode]);
  
  // ===== Server-side Pagination - Ürün yükleme =====
  useEffect(() => {
    if (!companyId) return;
    
    loadInventoryItemsPaged(
      companyId,
      itemsPage + 1,
      itemsRowsPerPage,
      itemSearchTerm || undefined,
      undefined,
      undefined,
      selectedWarehouseId || undefined,
      undefined,
      undefined,
      false,
      undefined
    );
  }, [companyId, itemsPage, itemsRowsPerPage, itemSearchTerm, selectedWarehouseId, loadInventoryItemsPaged]);
  
  // ===== Varsayılan depo seçimi =====
  useEffect(() => {
    if (warehouses.length > 0 && !selectedWarehouseId) {
      const defaultWarehouse = warehouses.find(wh => wh.isDefault);
      setSelectedWarehouseId(defaultWarehouse?.id || warehouses[0].id);
    }
  }, [warehouses, selectedWarehouseId]);
  
  // ===== E-Fatura entegrasyonu =====
  useEffect(() => {
    const loadEInvoiceIntegration = async () => {
      if (!ettnFromUrl || !companyId) return;
      
      setIsAccountMatchModalOpen(true);
      
      try {
        const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
        const result = integrationResponse.data;
        const sqlData = result.data;

        if (!sqlData) {
          setError('E-fatura entegrasyon bilgileri bulunamadı.');
          setIsAccountMatchModalOpen(false);
          return;
        }

        setEInvoiceUsername(sqlData.username || '');
        setEInvoicePassword(sqlData.password || '');

        const providerType = (sqlData.providerType || sqlData.ProviderType || 'finansbank').toString().toLowerCase();
        setActiveEInvoiceProvider(providerType);

        // EDM: UBL XML'i EDM gelen kutusundan çek, parse-einvoice'e doğrudan içerik olarak ver
        let edmXmlContent: string | null = null;
        if (providerType === 'edm') {
          try {
            const xmlResp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_CONTENT, {
              uuid: ettnFromUrl,
              id: null,
              type: 'XML',
              branchId: null,
            });
            const xmlData = xmlResp.data;
            if (xmlData?.success && xmlData?.found && xmlData?.content) {
              edmXmlContent = xmlData.content;
              setEInvoiceXmlContent(xmlData.content);
            } else {
              setError('EDM faturasının XML içeriği alınamadı.');
              setIsAccountMatchModalOpen(false);
              return;
            }
          } catch (xmlErr: any) {
            setError(
              xmlErr?.response?.data?.errorLong ||
              xmlErr?.response?.data?.error ||
              'EDM faturasının XML içeriği alınamadı.'
            );
            setIsAccountMatchModalOpen(false);
            return;
          }
        }

        // XML'i parse et
        const parseResponse = await accountingApi.post(ACCOUNTING_API.INVOICE_PARSE_EINVOICE, {
            ettn: ettnFromUrl,
            companyId: companyId,
            username: sqlData.username,
            password: sqlData.password,
            xmlContent: edmXmlContent,
          });

        {
          const parseResult = parseResponse.data;
          if (parseResult.success && parseResult.data) {
            if (parseResult.data.invoiceNumber) {
              setInvoiceNumber(parseResult.data.invoiceNumber);
            }
            if (parseResult.data.supplierInfo) {
              setEInvoiceSupplierInfo(parseResult.data.supplierInfo);
            }
          }
        }
      } catch (err: any) {
        setError(err.message || 'E-fatura yüklenirken hata oluştu.');
        setIsAccountMatchModalOpen(false);
      }
    };
    
    loadEInvoiceIntegration();
  }, [ettnFromUrl, companyId]);

  // ===== Aktif e-fatura entegratörünü belirle (iade gönderimi/persist için) =====
  // ettn akışı providerType'ı zaten setliyor; burada ettn yokken (ör. iade modu) belirleriz.
  useEffect(() => {
    if (ettnFromUrl || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
        const sqlData = resp.data?.data;
        if (cancelled || !sqlData) return;
        const providerType = (sqlData.providerType || sqlData.ProviderType || 'finansbank').toString().toLowerCase();
        setActiveEInvoiceProvider(providerType);
      } catch {
        // Entegrasyon yoksa sessiz geç — iade yine kaydedilir, gönderim ayrı adımda doğrulanır.
      }
    })();
    return () => { cancelled = true; };
  }, [ettnFromUrl, companyId]);

  // ===== Gönderilmiş bir alış faturasından iade oluşturma (localStorage'dan veri yükle) =====
  useEffect(() => {
    if (!isFromReturn || editingInvoiceId) return;
    const raw = localStorage.getItem('returnFromInvoice');
    if (!raw) {
      console.log('⚠️ [IADE-ALIS] localStorage\'da iade kaynağı bulunamadı');
      return;
    }
    try {
      const src = JSON.parse(raw);
      localStorage.removeItem('returnFromInvoice');
      console.log('📥 [IADE-ALIS] İade kaynağı yüklendi:', src);

      // İade tipi olarak işaretle (E-Fatura tip kodu = IADE)
      setEInvoiceTypeCode('IADE');

      // İade edilen orijinal (alış) fatura referansı
      const refId = src.returnedInvoiceId ?? src.id;
      const refNumber = src.returnedInvoiceNumber ?? src.invoiceNumber;
      const refDate = src.returnedInvoiceDate ?? src.invoiceDate;
      if (refId) setReturnedInvoiceId(String(refId));
      if (refNumber) setReturnedInvoiceNumber(String(refNumber));
      if (refDate) setReturnedInvoiceDate(String(refDate));

      // Cari hesabı ayarla (orijinal alış faturasındaki tedarikçi ile aynı olmalı)
      if (src.accountId) {
        setSelectedAccount({
          id: String(src.accountId),
          companyName: src.accountName || '',
          authorizedPerson: '',
          taxNumber: src.accountTaxNumber || '',
          taxOffice: src.accountTaxOffice || '',
          address: src.accountAddress || '',
          city: '',
          district: '',
          phone: src.accountPhone || '',
          email: src.accountEmail || '',
        } as Account);
      }

      // Para birimi
      if (src.currency) setCurrency(src.currency);

      // PYS: iade faturası aynı projeye bağlanır (kartlarda ters işaretle düşer)
      setProjectId(src.projectId ?? null);

      // Kalemleri kopyala (orijinal fatura kalemleri zaten hesaplanmış değerlere sahip)
      if (Array.isArray(src.items) && src.items.length > 0) {
        const items: InvoiceItem[] = src.items.map((it: any, index: number) => {
          const quantity = it.quantity || 1;
          const unitPrice = it.unitPrice || 0;
          const vatRate = it.vatRate || 0;
          const priceType = (it.priceType || 'VAT_EXCLUDED') as 'VAT_EXCLUDED' | 'VAT_INCLUDED';
          const enteredPrice = it.enteredPrice || unitPrice;
          return {
            id: `return_item_${index}_${Date.now()}`,
            itemId: it.itemId || `return_item_${index}`,
            itemName: it.itemName || 'Ürün',
            quantity,
            displayQuantity: formatQuantityForDisplay(quantity),
            unit: it.unit || 'adet',
            priceType,
            enteredPrice,
            unitPrice,
            unitPriceWithVat: it.unitPriceWithVat || Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100,
            vatRate,
            sctRate: it.sctRate || 0,
            discountRate: it.discountRate || 0,
            discountAmount: it.discountAmount || 0,
            lineTotal: it.lineTotal || it.netTotal || 0,
            netTotal: it.netTotal || 0,
            vatAmount: it.vatAmount || 0,
            sctAmount: it.sctAmount || 0,
            grossTotal: it.grossTotal || 0,
            warehouseId: it.warehouseId || '',
            ...readInvoiceLot(it),
          } as InvoiceItem;
        });
        setSelectedItems(items);
        console.log('✅ [IADE-ALIS] Orijinal fatura kalemleri kopyalandı:', items.length);
      }

      // ✅ Tevkifat/Stopaj — orijinal alış faturasından taşı (TEVKIFATIADE için)
      if (src.tevkifatRate && String(src.tevkifatRate) !== "0") {
        setShowTevkifat(true);
        setTevkifatRate(String(src.tevkifatRate));
        if (src.tevkifatType) setTevkifatType(String(src.tevkifatType));
        setEInvoiceTypeCode('TEVKIFATIADE');
      }
      if (src.stopajRate && Number(src.stopajRate) > 0) {
        setShowStopaj(true);
        setStopajRate(Number(src.stopajRate));
      }

      // Bilgilendirme notu
      if (refNumber) {
        setNotes((prev) => prev ? prev : `Alış iade faturası — Orijinal fatura: ${refNumber}`);
      }
    } catch (err) {
      console.error('❌ [IADE-ALIS] İade kaynağı parse edilemedi:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromReturn, editingInvoiceId]);
  
  // ===== Otomatik cari seçim modalı =====
  useEffect(() => {
    if (ettnFromUrl || isFromReturn) return;
    if (editingInvoiceId || loading || dataLoading) return;
    if (!selectedAccount && !isDraftModalOpen && companyId) {
      const timer = setTimeout(() => setIsAccountModalOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [editingInvoiceId, loading, dataLoading, selectedAccount, isDraftModalOpen, companyId, ettnFromUrl, isFromReturn]);
  
  // ===== Handle account selected from E-Invoice modal =====
  const handleAccountSelected = (account: Account) => {
    setSelectedAccount(account);
    setIsAccountMatchModalOpen(false);
    setTimeout(() => setIsEInvoiceModalOpen(true), 300);
  };
  
  // ===== PDF items handler =====
  const handlePdfItemsLoaded = (pdfItems: any[]) => {
    const calculatedItems = pdfItems.map(item => recalculateLineItem(item));
    setSelectedItems(prev => [...prev, ...calculatedItems]);
  };
  
  // ===== Success modal close =====
  const handleSuccessModalClose = () => {
    // Bekleyen otomatik yönlendirmeyi iptal et; aksi halde fatura↔teklif sekme döngüsü oluşur
    clearSuccessNavTimer();
    setIsSuccessModalOpen(false);
    
    if (!editingInvoiceId && !isOfferMode && !isOrderMode) {
      setSelectedAccount(null);
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      setDueDate('');
      setSelectedItems([]);
      setNotes('');
      setCurrency('TRY');
      setExchangeRate('1');
      setTevkifatRate("0");
      setTevkifatType("");
      setShowTevkifat(false);
      setStopajRate(0);
      setShowStopaj(false);
      
      if (companyId) {
        generateInvoiceNumber(companyId).then(setInvoiceNumber);
      }
    }
    
    leaveForm();
  };
  
  // ===== Taslak uygula =====
  const handleApplyDraft = () => {
    if (!draftData) return;
    
    applyDraftToForm(draftData, {
      setSelectedAccount,
      setInvoiceDate,
      setDueDate,
      setSelectedItems,
      setNotes,
      setCurrency,
      setExchangeRate,
      setTevkifatRate,
      setTevkifatAmount: () => {}, // Hesaplanacak
      setTevkifatType,
      setShowTevkifat,
      setStopajRate,
      setStopajAmount: () => {}, // Hesaplanacak
      setShowStopaj,
      setOriginalInvoiceTotal,
    });
  };
  
  // ===== Taslak kaydet handler =====
  const handleSaveDraft = (silent: boolean = false) => {
    saveDraftToLocalStorage({
      selectedAccount,
      invoiceDate,
      dueDate,
      selectedItems,
      notes,
      currency,
      exchangeRate,
      tevkifatRate,
      tevkifatAmount,
      tevkifatType,
      showTevkifat,
      stopajRate,
      stopajAmount,
      showStopaj,
      originalInvoiceTotal,
    }, silent);
  };

  // ✅ Otomatik taslak: form doluyken her değişiklikte sessizce kaydet.
  // Sekme değişimi/sayfa kapanması ile kalemlerin kaybolmasını önler; form yeniden
  // açıldığında "kaydedilmiş taslak var" penceresi ile geri yüklenir.
  // Not: `loading` bilerek bağımlılıklarda değil — kayıt sonrası loading değişimi
  // temizlenen taslağı yeniden yazmasın diye yalnızca form verisi değişince çalışır.
  useEffect(() => {
    if (!companyId || editingInvoiceId || loading || isDraftModalOpen) return;
    const hasContent = !!selectedAccount || selectedItems.length > 0 || !!notes.trim();
    setFormDirty(hasContent);
    if (!hasContent) return;
    const t = setTimeout(() => handleSaveDraft(true), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, editingInvoiceId, selectedAccount, selectedItems, notes, invoiceDate, dueDate, currency, exchangeRate, tevkifatRate, tevkifatType, stopajRate]);

  // Düzenleme modunda da form doluysa ayrılırken onay sorulsun
  useEffect(() => {
    if (!editingInvoiceId) return;
    setFormDirty(selectedItems.length > 0 || !!selectedAccount);
  }, [editingInvoiceId, selectedItems, selectedAccount]);

  useEffect(() => () => setFormDirty(false), []);
  
  // ===== Depo değiştirme handler =====
  const handleWarehouseChange = (event: SelectChangeEvent) => {
    const warehouseId = event.target.value;
    setSelectedWarehouseId(warehouseId === "all" ? null : warehouseId);
  };
  
  // ===== Loading state =====
  if ((loading || dataLoading) && editingInvoiceId) {
    return (
      <Box sx={{ 
        width: '100%',
        p: 2,
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {tt(`${docNoun.tr} Hazırlanıyor...`, `Preparing ${docNoun.en}...`)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tt(`${docNoun.tr} kalemleri ve hesaplamalar yükleniyor`, `${docNoun.en} items and calculations are loading`)}
        </Typography>
      </Box>
    );
  }

  // ===== RENDER =====
  return (
    <Box sx={{ width: '100%', p: 2, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <InvoiceHeader sx={{ flexShrink: 0, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBackDynamic}
            sx={{ mr: 2 }}
          >
            {tt('Geri', 'Back')}
          </Button>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {dynamicPageTitle}
          </Typography>
          <GuideButton guideKey="documents.purchaseInvoice" sx={{ ml: 1 }} />
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<DraftsIcon />}
            disabled={loading || (!selectedAccount && selectedItems.length === 0)}
            sx={{ mr: 1 }}
            onClick={() => handleSaveDraft()}
          >
            {tt('Taslak Kaydet', 'Save Draft')}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            disabled={loading || dataLoading}
            sx={{ mr: 1 }}
            onClick={() => handleSaveInvoice()}
          >
            {editingInvoiceId ? tt('Güncelle', 'Update') : tt('Kaydet', 'Save')}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<PrintIcon />}
            disabled={loading || selectedItems.length === 0}
            sx={{ mr: 1 }}
          >
            {tt('Yazdır', 'Print')}
          </Button>
          {/* PYS: belgeyi projeye bağlama (tüm modlarda, opsiyonel) — düğme + modal */}
          <ProjectPickerButton value={projectId} onChange={setProjectId} disabled={loading} />
        </Box>
      </InvoiceHeader>
      
      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, flexShrink: 0, whiteSpace: 'pre-line' }}
        >
          {error}
        </Alert>
      )}
      
      {showDraftSavedAlert && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }}>
          {tt('Taslak başarıyla kaydedildi! 💾', 'Draft saved successfully! 💾')}
        </Alert>
      )}

      {orderStockHandedOver && !editingInvoiceId && (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
          {tt(ORDER_STOCK_HANDOVER_MESSAGE, 'Stock was processed at the order stage; when the invoice is saved, the order movement is transferred to the invoice.')}
        </Alert>
      )}
      
      {(loading || dataLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, flexShrink: 0 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Üst Bölüm - Cari ve Fatura Bilgileri */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Sol - Cari Bilgileri */}
        <Grid item xs={12} md={6}>
          <InvoiceSection>
            <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
              {tt('Cari Bilgileri', 'Account Information')}
            </SectionTitle>
            
            {selectedAccount ? (
              <Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={tt('Ünvan / Hesap Adı', 'Title / Account Name')}
                      value={selectedAccount.companyName}
                      onChange={(e) => setSelectedAccount({...selectedAccount, companyName: e.target.value})}
                      sx={{ mb: 0.5 }}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={tt('Yetkili Kişi', 'Authorized Person')}
                      value={selectedAccount.authorizedPerson}
                      onChange={(e) => setSelectedAccount({...selectedAccount, authorizedPerson: e.target.value})}
                      sx={{ mb: 0.5 }}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label={tt('Vergi No', 'Tax ID')}
                      value={selectedAccount.taxNumber}
                      onChange={(e) => setSelectedAccount({...selectedAccount, taxNumber: e.target.value})}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label={tt('Vergi Dairesi', 'Tax Office')}
                      value={selectedAccount.taxOffice}
                      onChange={(e) => setSelectedAccount({...selectedAccount, taxOffice: e.target.value})}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label={tt('İlçe', 'District')}
                      value={selectedAccount.district}
                      onChange={(e) => setSelectedAccount({...selectedAccount, district: e.target.value})}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label={tt('Şehir', 'City')}
                      value={selectedAccount.city}
                      onChange={(e) => setSelectedAccount({...selectedAccount, city: e.target.value})}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label={tt('Telefon', 'Phone')}
                      value={selectedAccount.phone}
                      onChange={(e) => setSelectedAccount({...selectedAccount, phone: e.target.value})}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={9}>
                    <TextField
                      fullWidth
                      label={tt('Adres', 'Address')}
                      value={selectedAccount.address}
                      onChange={(e) => setSelectedAccount({...selectedAccount, address: e.target.value})}
                      size="small"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 0.5 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="small" 
                    onClick={handleOpenAccountModal}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {tt('Farklı Cari Seç', 'Select Different Account')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<PersonIcon />}
                  onClick={handleOpenAccountModal}
                >
                  {tt('Cari Seç', 'Select Account')}
                </Button>
              </Box>
            )}
          </InvoiceSection>
        </Grid>
        
        {/* Sağ - Fatura Bilgileri */}
        <Grid item xs={12} md={6}>
          <InvoiceSection>
            <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
              {tt(`${docNoun.tr} Bilgileri`, `${docNoun.en} Information`)}
            </SectionTitle>
            
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={tt(`${docNoun.tr} No`, `${docNoun.en} No`)}
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  disabled={loading}
                  size="small"
                  placeholder={tt('Otomatik oluşturulacak', 'Will be generated automatically')}
                  inputProps={{ style: { fontSize: '0.75rem' } }}
                />
              </Grid>
              {isInvoiceMode && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.75rem' }}>{tt('Şube', 'Branch')}</InputLabel>
                    <Select
                      value={invoiceBranchId || ''}
                      onChange={(e) => setInvoiceBranchId(e.target.value ? String(e.target.value) : null)}
                      label={tt('Şube', 'Branch')}
                      disabled={loading || isManager}
                      displayEmpty
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {!isManager && (
                        <MenuItem value=""><em>{tt('Merkez (şubesiz)', 'Headquarters (no branch)')}</em></MenuItem>
                      )}
                      {invoiceBranchId && !branchOptions.some(b => b.id === invoiceBranchId) && (
                        <MenuItem value={invoiceBranchId}>{invoiceBranchId}</MenuItem>
                      )}
                      {branchOptions.map(b => (
                        <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {hideWarehouseUi && (
                    <Typography variant="caption" color="text.secondary">
                      {tt('Bu şubeye bağlı depo yok; fatura stok hareketi oluşturmadan kaydedilir.',
                        'This branch has no warehouse; the invoice is saved without stock movements.')}
                    </Typography>
                  )}
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <AppDatePicker
                  fullWidth
                  label={tt(`${docNoun.tr} Tarihi`, `${docNoun.en} Date`)}
                  value={invoiceDate}
                  onChange={setInvoiceDate}
                  disabled={loading}
                  size="small"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppDatePicker
                  fullWidth
                  label={tt('Vade Tarihi', 'Due Date')}
                  value={dueDate}
                  onChange={setDueDate}
                  disabled={loading}
                  size="small"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>{tt('Para Birimi', 'Currency')}</InputLabel>
                  <Select
                    value={currency}
                    onChange={handleCurrencyChange}
                    label={tt('Para Birimi', 'Currency')}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {availableCurrencies.map(curr => (
                      <MenuItem key={curr.code} value={curr.code}>
                        {curr.code} ({getCurrencySymbol(curr.code)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tt('Döviz Kuru', 'Exchange Rate')}
                  type="number"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  disabled={currency === 'TRY'}
                  size="small"
                  inputProps={{ min: "0", step: "0.0001", style: { fontSize: '0.75rem' } }}
                  InputProps={{
                    startAdornment: currency !== 'TRY' ? (
                      <InputAdornment position="start">
                        <Typography variant="caption">1 {currency} =</Typography>
                      </InputAdornment>
                    ) : undefined,
                    endAdornment: currency !== 'TRY' ? (
                      <InputAdornment position="end">
                        <Typography variant="caption">₺</Typography>
                      </InputAdornment>
                    ) : undefined
                  }}
                />
              </Grid>
              {isInvoiceMode && (
                <Grid item xs={12}>
                  <ExpenseCategorySelect
                    value={expenseCategoryId}
                    onChange={setExpenseCategoryId}
                    fallbackName={expenseCategoryName}
                    label={tt('Gider türü (opsiyonel)', 'Expense type (optional)')}
                    helperText={tt('Boş bırakılırsa stok/mal alımı (maliyet) sayılır', 'If left empty, counted as stock/goods purchase (cost)')}
                    disabled={loading}
                  />
                </Grid>
              )}
            </Grid>
          </InvoiceSection>
        </Grid>
      </Grid>
      
      {/* ✅ TEKLİF MODU - GEÇERLİLİK TARİHİ VE DURUM */}
      {isOfferMode && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <InvoiceSection>
              <SectionTitle variant="h6" sx={{ fontSize: '1rem' }}>{tt('📋 Teklif Bilgileri', '📋 Offer Information')}</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <AppDatePicker
                    fullWidth
                    label={tt('Geçerlilik Tarihi *', 'Validity Date *')}
                    value={validUntil}
                    onChange={setValidUntil}
                    disabled={loading}
                    size="small"
                    helperText={tt('Teklifin geçerli olduğu son tarih', 'Last date when this offer is valid')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{tt('Teklif Durumu', 'Offer Status')}</InputLabel>
                    <Select
                      value={offerStatus}
                      onChange={(e) => setOfferStatus(e.target.value as 'draft' | 'pending' | 'approved' | 'rejected')}
                      label={tt('Teklif Durumu', 'Offer Status')}
                      disabled={loading}
                    >
                      <MenuItem value="draft">{tt('Taslak', 'Draft')}</MenuItem>
                      <MenuItem value="pending">{tt('Beklemede', 'Pending')}</MenuItem>
                      <MenuItem value="approved">{tt('Onaylandı', 'Approved')}</MenuItem>
                      <MenuItem value="rejected">{tt('Reddedildi', 'Rejected')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </InvoiceSection>
          </Grid>
        </Grid>
      )}
      
      {/* ✅ SİPARİŞ MODU - TESLİM TARİHİ VE DURUM */}
      {isOrderMode && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <InvoiceSection>
              <SectionTitle variant="h6" sx={{ fontSize: '1rem' }}>{tt('📋 Sipariş Bilgileri', '📋 Order Information')}</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <AppDatePicker
                    fullWidth
                    label={tt('Teslim Tarihi', 'Delivery Date')}
                    value={deliveryDate}
                    onChange={setDeliveryDate}
                    disabled={loading}
                    size="small"
                    helperText={tt('Opsiyonel - Tahmini teslim tarihi', 'Optional - estimated delivery date')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{tt('Sipariş Durumu', 'Order Status')}</InputLabel>
                    <Select
                      value={orderStatus}
                      onChange={(e) => setOrderStatus(e.target.value as any)}
                      label={tt('Sipariş Durumu', 'Order Status')}
                      disabled={loading}
                    >
                      <MenuItem value="draft">{tt('Taslak', 'Draft')}</MenuItem>
                      <MenuItem value="pending">{tt('Beklemede', 'Pending')}</MenuItem>
                      <MenuItem value="confirmed">{tt('Onaylandı', 'Confirmed')}</MenuItem>
                      <MenuItem value="shipped">{tt('Sevk Edildi', 'Shipped')}</MenuItem>
                      <MenuItem value="delivered">{tt('Teslim Edildi', 'Delivered')}</MenuItem>
                      <MenuItem value="canceled">{tt('İptal', 'Canceled')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </InvoiceSection>
          </Grid>
        </Grid>
      )}
      
      {/* Fatura Kalemleri */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InvoiceSection>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
                {tt(`${docNoun.tr} Kalemleri`, `${docNoun.en} Items`)}
              </SectionTitle>
              <Box>
                <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', mr: 1, mb: { xs: 1, md: 0 } }}>
                  <BarcodeScanField
                    disabled={loading}
                    onScanned={(scanned: ScannedInvoiceItem) =>
                      setSelectedItems(prev => addScannedLine(prev, scanned, { warehouseId: selectedWarehouseId, priceSource: 'purchase' }))}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenItemModal}
                  disabled={loading}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {tt('Ürün Ekle', 'Add Item')}
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<NotListedLocationIcon />}
                  onClick={handleAddManualItem}
                  disabled={loading}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {tt('Stoksuz Ürün Ekle', 'Add Non-Stock Item')}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => setIsCopyFromDocumentModalOpen(true)}
                  disabled={loading}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {tt(
                    `Başka ${mode === 'offer' ? 'Teklifden' : mode === 'order' ? 'Siparişten' : 'Faturadan'}`,
                    `From Another ${mode === 'offer' ? 'Offer' : mode === 'order' ? 'Order' : 'Invoice'}`
                  )}
                </Button>
                <Button 
                  variant="outlined" 
                  color="error" 
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => setIsPdfModalOpen(true)}
                  disabled={loading}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {tt("PDF'den Yükle", 'Upload from PDF')}
                </Button>
                {!hideWarehouseUi && (
                <Button 
                  variant="outlined" 
                  color="info" 
                  startIcon={<WarehouseIcon />}
                  onClick={() => setIsBulkWarehouseModalOpen(true)}
                  disabled={loading || selectedItems.length === 0}
                  size="small"
                >
                  {tt('Toplu Depo Seç', 'Bulk Warehouse Select')}
                </Button>
                )}
              </Box>
            </Box>
            
            {/* Fatura Kalemleri Tablosu */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', width: 40 }}>{tt('Sıra', 'No')}</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', minWidth: 200 }}>{tt('Ürün', 'Item')}</TableCell>
                    {showInvoiceLots && <InvoiceLotHeaders />}
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('Miktar', 'Quantity')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('Birim', 'Unit')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'warning.lighter', fontWeight: 'bold' }}>{tt('Fiyatı', 'Price')}</TableCell>
                    <TableCell align="center" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>KDV</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>KDV %</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('KDV Hariç', 'Excl. VAT')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'success.lighter', fontWeight: 'bold' }}>{tt('KDV Dahil', 'Incl. VAT')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 0.5, fontSize: '0.75rem' }}>
                      <FormControl size="small" variant="standard" sx={{ minWidth: 92 }}>
                        <Select
                          value={discountMode}
                          onChange={(e) => handleDiscountModeChange(e.target.value as 'PERCENT' | 'AMOUNT')}
                          disableUnderline
                          sx={{ fontSize: '0.75rem', fontWeight: 'bold', '& .MuiSelect-select': { py: 0, pr: '20px !important' } }}
                        >
                          <MenuItem value="PERCENT" sx={{ fontSize: '0.75rem' }}>{tt('İndirim %', 'Discount %')}</MenuItem>
                          <MenuItem value="AMOUNT" sx={{ fontSize: '0.75rem' }}>{tt(`İndirim ${getCurrencySymbol(currency)}`, `Discount ${getCurrencySymbol(currency)}`)}</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'grey.100', fontWeight: 'bold' }}>{tt('Tutar (KDV Hariç)', 'Amount (Excl. VAT)')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'info.lighter', fontWeight: 'bold' }}>{tt('Tutar (KDV Dahil)', 'Amount (Incl. VAT)')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('KDV Tutarı', 'VAT Amount')}</TableCell>
                    {currency !== 'TRY' && (
                      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>TRY</TableCell>
                    )}
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('İşlem', 'Action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={(currency !== 'TRY' ? 15 : 14) + (showInvoiceLots ? 3 : 0)} align="center" sx={{ fontSize: '0.85rem', py: 2 }}>
                        {tt('Henüz ürün eklenmedi. Lütfen "Ürün Ekle" butonuna tıklayın.', 'No items added yet. Please click "Add Item".')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedItems.map((item, index) => {
                      const isManual = item.itemId.startsWith('manual_');
                      return (
                      <TableRow key={item.id} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: lotErrorLine === index + 1 ? 'error.lighter' : undefined }}>
                        <TableCell align="center" sx={{ py: 0.75, px: 1, fontSize: '0.8rem', fontWeight: 'bold', color: 'primary.main' }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 1 }}>
                          {isManual ? (
                            <TextField
                              size="small"
                              value={item.itemName}
                              onChange={(e) => handleItemNameChange(item.id, e.target.value)}
                              placeholder={tt('Ürün adı girin', 'Enter item name')}
                              sx={{ width: '100%', minWidth: 180, '& input': { fontSize: '0.85rem' } }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '0.85rem' }}>{item.itemName}</Typography>
                          )}
                        </TableCell>
                        {showInvoiceLots && (
                          <InvoiceLotCells
                            item={item}
                            mode={purchaseLotMode}
                            highlight={lotErrorLine === index + 1}
                            onChange={handleLotChange}
                            onSplit={handleSplitLot}
                          />
                        )}

                        {/* Miktar */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          <InvoiceNumberField
                            text={item.displayQuantity ?? formatQuantityForDisplay(item.quantity)}
                            onTextChange={(raw) => handleDisplayQuantityChange(item.id, raw)}
                            onCommit={() => handleQuantityCommitOnBlur(item.id)}
                            invalid={attemptedSave && isFieldEmpty(item, 'quantity')}
                            width={90}
                          />
                          {packageHint(item) && (
                            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {packageHint(item)}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Birim */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          <Autocomplete
                            freeSolo
                            size="small"
                            options={unitOptions}
                            getOptionLabel={(option) => localizeUnit(option)}
                            renderOption={(props, option) => (
                              <li {...props}>{localizeUnit(option)}</li>
                            )}
                            value={item.unit}
                            onChange={(_e, newValue) => handleUnitChange(item.id, normalizeUnitInput((newValue as string) || ''))}
                            onInputChange={(_e, newValue) => handleUnitChange(item.id, normalizeUnitInput(newValue || ''))}
                            disabled={!isManual}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={tt('Birim', 'Unit')}
                                sx={{
                                  bgcolor: isManual ? 'background.paper' : 'action.disabledBackground',
                                  '& input': { fontSize: '0.85rem' },
                                }}
                              />
                            )}
                            sx={{ width: 110 }}
                            disableClearable
                          />
                        </TableCell>

                        {/* Fiyatı (enteredPrice) */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          <InvoiceNumberField
                            text={displayTextFor(item, 'price')}
                            onTextChange={(raw) => handleItemDisplayChange(item.id, 'price', raw)}
                            onCommit={() => handleItemNumberCommit(item.id, 'price')}
                            suffix={getCurrencySymbol(currency)}
                            invalid={attemptedSave && isFieldEmpty(item, 'price')}
                            width={125}
                            sx={{ bgcolor: 'warning.lighter' }}
                          />
                        </TableCell>

                        {/* KDV Dropdown (priceType) */}
                        <TableCell align="center" sx={{ py: 0.75, px: 1 }}>
                          <FormControl size="small" sx={{ width: 120 }}>
                            <Select
                              value={item.priceType}
                              onChange={(e) => handlePriceTypeChange(item.id, e.target.value as 'VAT_EXCLUDED' | 'VAT_INCLUDED')}
                              size="small"
                              sx={{ fontSize: '0.85rem' }}
                            >
                              <MenuItem value="VAT_EXCLUDED" sx={{ fontSize: '0.85rem' }}>{tt('Hariç', 'Excluded')}</MenuItem>
                              <MenuItem value="VAT_INCLUDED" sx={{ fontSize: '0.85rem' }}>{tt('Dahil', 'Included')}</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>

                        {/* KDV % */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          <InvoiceNumberField
                            text={displayTextFor(item, 'vatRate')}
                            onTextChange={(raw) => handleItemDisplayChange(item.id, 'vatRate', raw)}
                            onCommit={() => handleItemNumberCommit(item.id, 'vatRate')}
                            suffix="%"
                            invalid={attemptedSave && isFieldEmpty(item, 'vatRate')}
                            width={85}
                          />
                        </TableCell>

                        {/* KDV Hariç */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {formatAmount(item.unitPrice)} {getCurrencySymbol(currency)}
                          </Typography>
                        </TableCell>

                        {/* KDV Dahil */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1, bgcolor: 'success.lighter' }}>
                          <Typography sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {formatAmount(item.unitPriceWithVat)} {getCurrencySymbol(currency)}
                          </Typography>
                        </TableCell>

                        {/* İndirim (kolon moduna göre % veya para) */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          {discountMode === 'AMOUNT' ? (
                            <InvoiceNumberField
                              text={displayTextFor(item, 'discountAmount')}
                              onTextChange={(raw) => handleItemDisplayChange(item.id, 'discountAmount', raw)}
                              onCommit={() => handleItemNumberCommit(item.id, 'discountAmount')}
                              suffix={getCurrencySymbol(currency)}
                              width={110}
                            />
                          ) : (
                            <InvoiceNumberField
                              text={displayTextFor(item, 'discountRate')}
                              onTextChange={(raw) => handleItemDisplayChange(item.id, 'discountRate', raw)}
                              onCommit={() => handleItemNumberCommit(item.id, 'discountRate')}
                              suffix="%"
                              width={85}
                            />
                          )}
                        </TableCell>

                        {/* Tutar (KDV Hariç) */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1, bgcolor: 'grey.100' }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {formatAmount(item.netTotal)} {getCurrencySymbol(currency)}
                          </Typography>
                        </TableCell>

                        {/* Tutar (KDV Dahil) */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1, bgcolor: 'info.lighter' }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {formatAmount(item.grossTotal)} {getCurrencySymbol(currency)}
                          </Typography>
                        </TableCell>

                        {/* KDV Tutarı */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1, fontSize: '0.85rem', color: 'success.main', whiteSpace: 'nowrap' }}>
                          {formatAmount(item.vatAmount)} {getCurrencySymbol(currency)}
                        </TableCell>

                        {currency !== 'TRY' && (
                          <TableCell align="right" sx={{ py: 0.75, px: 1, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(item.grossTotal * (parseFloat(exchangeRate) || 0))}
                          </TableCell>
                        )}

                        {/* İşlem: depo seçimi + silme */}
                        <TableCell align="right" sx={{ py: 0.75, px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {!isManual && !hideWarehouseUi && (
                              <FormControl size="small" sx={{ width: 130, mr: 1 }}>
                                <Select
                                  value={item.warehouseId || ""}
                                  onChange={(e) => handleWarehouseChangeForItem(item.id, e.target.value)}
                                  size="small"
                                  displayEmpty
                                  sx={{ fontSize: '0.85rem' }}
                                >
                                  <MenuItem value="" disabled>{tt('Depo Seç', 'Select Warehouse')}</MenuItem>
                                  {getWarehousesForItem(item.itemId).map(warehouse => (
                                    <MenuItem key={warehouse.id} value={warehouse.id} sx={{ fontSize: '0.85rem' }}>
                                      {warehouse.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Orijinal Fatura Tutarı */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Orijinal Fatura Genel Toplamı"
                    value={originalInvoiceTotal}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.,]/g, '');
                      setOriginalInvoiceTotal(value);
                    }}
                    placeholder="Örn: 1494107,50"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                            {getCurrencySymbol(currency)}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      originalInvoiceTotal && invoiceDifference !== 0
                        ? `Fark: ${invoiceDifference > 0 ? '+' : ''}${formatCurrency(invoiceDifference)}`
                        : 'Opsiyonel - Yuvarlama farkı varsa girin'
                    }
                  />
                </Grid>
              </Grid>
            </Box>
            
            {/* Notlar ve Toplamlar */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box>
                  <SectionTitle variant="h6" sx={{ fontSize: '0.9rem', mb: 1 }}>
                    {tt('Notlar', 'Notes')}
                  </SectionTitle>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={tt(`${docNoun.tr} ile ilgili notlar...`, `Notes about the ${docNoun.en.toLowerCase()}...`)}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={loading}
                    size="small"
                    inputProps={{ style: { fontSize: '0.75rem' } }}
                  />
                </Box>
                
                {/* Tevkifat */}
                {showTevkifat && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mr: 1 }}>
                        Tevkifat Bilgileri
                      </Typography>
                      <IconButton size="small" color="error" onClick={handleRemoveTevkifat} sx={{ p: 0 }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Tevkifat Tipi:</Typography>
                      <FormControl size="small" sx={{ width: '70%' }}>
                        <Select
                          value={tevkifatType}
                          onChange={handleTevkifatTypeChange}
                          size="small"
                          sx={{ fontSize: '0.75rem' }}
                          displayEmpty
                        >
                          <MenuItem value=""><em>Seçiniz</em></MenuItem>
                          {tevkifatTypes.map((type) => (
                            <MenuItem key={type.code} value={type.code} sx={{ fontSize: '0.75rem' }}>
                              {type.code} - {type.description.substring(0, 15)}...
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Tevkifat Oranı:</Typography>
                      <FormControl size="small" sx={{ width: '70%' }}>
                        <Select value={tevkifatRate} onChange={handleTevkifatRateChange} size="small" sx={{ fontSize: '0.75rem' }}>
                          <MenuItem value="0">Yok</MenuItem>
                          {['1/10','2/10','3/10','4/10','5/10','6/10','7/10','8/10','9/10','10/10'].map(r => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                )}
                
                {/* Stopaj */}
                {showStopaj && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mr: 1 }}>
                        Stopaj Bilgileri
                      </Typography>
                      <IconButton size="small" color="error" onClick={handleRemoveStopaj} sx={{ p: 0 }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Stopaj Oranı (%):</Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={stopajRate}
                        onChange={handleStopajRateChange}
                        inputProps={{ min: 0, max: 100, step: 1, style: { fontSize: '0.75rem', padding: '2px 4px' } }}
                        sx={{ width: '70%' }}
                      />
                    </Box>
                  </Box>
                )}
              </Grid>
              
              {/* Toplamlar */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <SectionTitle variant="h6" sx={{ fontSize: '0.9rem' }}>{tt('Toplamlar', 'Totals')}</SectionTitle>
                    <IconButton size="small" color="primary" onClick={handleToggleAdditionalMenu}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Box>
                    <TotalLine>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('Ara Toplam:', 'Subtotal:')}</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {formatAmount(subtotal)} {getCurrencySymbol(currency)}
                      </Typography>
                    </TotalLine>
                    
                    {discountNetTotal > 0 && (
                      <TotalLine>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('İndirim:', 'Discount:')}</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error" sx={{ fontSize: '0.75rem' }}>
                          -{formatAmount(discountNetTotal)} {getCurrencySymbol(currency)}
                        </Typography>
                      </TotalLine>
                    )}
                    
                    {discountNetTotal > 0 && (
                      <TotalLine>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('İndirim Sonrası Ara Toplam:', 'Subtotal After Discount:')}</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                          {formatAmount(netSubtotal)} {getCurrencySymbol(currency)}
                        </Typography>
                      </TotalLine>
                    )}
                    
                    {Object.entries(vatRateTotals).map(([rate, amount]) => (
                      <TotalLine key={`vat-${rate}`}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>KDV %{rate}:</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                          {formatAmount(amount)} {getCurrencySymbol(currency)}
                        </Typography>
                      </TotalLine>
                    ))}
                    
                    <TotalLine>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('KDV Toplam:', 'VAT Total:')}</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {formatAmount(vatTotal)} {getCurrencySymbol(currency)}
                      </Typography>
                    </TotalLine>
                    
                    {originalInvoiceTotal && invoiceDifference !== 0 && (
                      <TotalLine>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'warning.dark' }}>Fatura Farkı:</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', color: invoiceDifference > 0 ? 'success.main' : 'error.main' }}>
                          {invoiceDifference > 0 ? '+' : ''}{formatCurrency(invoiceDifference)}
                        </Typography>
                      </TotalLine>
                    )}
                    
                    {showTevkifat && tevkifatRate !== "0" && (
                      <TotalLine>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Tevkifat ({tevkifatRate}):</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error" sx={{ fontSize: '0.75rem' }}>
                          {formatAmount(tevkifatAmount)} {getCurrencySymbol(currency)}
                        </Typography>
                      </TotalLine>
                    )}
                    
                    {showStopaj && stopajRate > 0 && (
                      <TotalLine>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Stopaj (%{stopajRate}):</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error" sx={{ fontSize: '0.75rem' }}>
                          {formatAmount(stopajAmount)} {getCurrencySymbol(currency)}
                        </Typography>
                      </TotalLine>
                    )}
                    
                    {sctTotal > 0 && (
                      <TotalLine>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>ÖTV Toplam:</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                          {formatAmount(sctTotal)} {getCurrencySymbol(currency)}
                        </Typography>
                      </TotalLine>
                    )}
                    
                    <Divider sx={{ my: 0.5 }} />
                    
                    <TotalLine>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{tt('Genel Toplam:', 'Grand Total:')}</Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ fontSize: '0.9rem' }}>
                        {formatAmount(finalGrandTotal)} {getCurrencySymbol(currency)}
                      </Typography>
                    </TotalLine>
                    
                    {currency !== 'TRY' && (
                      <TotalLine>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>TL Karşılığı:</Typography>
                        <Typography variant="subtitle2" color="primary" sx={{ fontSize: '0.8rem' }}>
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(finalGrandTotal * parseFloat(exchangeRate))}
                        </Typography>
                      </TotalLine>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </InvoiceSection>
        </Grid>
      </Grid>
      
      <Box sx={{ height: '50px', flexShrink: 0 }} />
      
      {/* Ek Menü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleAddTevkifat} disabled={showTevkifat}>
          <AddCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Tevkifat Ekle', 'Add Withholding')}
        </MenuItem>
        <MenuItem onClick={handleAddStopaj} disabled={showStopaj}>
          <AddCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Stopaj Ekle', 'Add Income Tax')}
        </MenuItem>
      </Menu>
      
      {/* ===== MODALS ===== */}
      
      {/* Cari Seçim Modalı */}
      <Dialog open={isAccountModalOpen} onClose={handleCloseAccountModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {tt('Cari Seçimi', 'Account Selection')}
            <IconButton onClick={handleCloseAccountModal}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            placeholder={tt('Cari adı, vergi no veya telefon ile arama yapın...', 'Search by account name, tax ID or phone...')}
            value={accountSearchTerm}
            onChange={handleAccountSearch}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ mb: 2 }}
          />
          
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tt('Hesap Adı', 'Account Name')}</TableCell>
                  <TableCell>{tt('Yetkili Kişi', 'Authorized Person')}</TableCell>
                  <TableCell>{tt('Vergi No', 'Tax ID')}</TableCell>
                  <TableCell>{tt('Telefon', 'Phone')}</TableCell>
                  <TableCell>{tt('İşlem', 'Action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">{tt('Cari hesap bulunamadı.', 'No account found.')}</TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts
                    .slice(accountsPage * accountsRowsPerPage, accountsPage * accountsRowsPerPage + accountsRowsPerPage)
                    .map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.companyName}</TableCell>
                        <TableCell>{account.authorizedPerson}</TableCell>
                        <TableCell>{account.taxNumber}</TableCell>
                        <TableCell>{account.phone}</TableCell>
                        <TableCell>
                          <Button size="small" variant="contained" color="primary" onClick={() => formSelectAccount(account)}>
                            {tt('Seç', 'Select')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredAccounts.length}
            page={accountsPage}
            onPageChange={handleAccountsPageChange}
            rowsPerPage={accountsRowsPerPage}
            onRowsPerPageChange={handleAccountsRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={tt('Sayfa başına satır:', 'Rows per page:')}
            sx={{ mt: 2 }}
          />
        </DialogContent>
      </Dialog>
      
      {/* Ürün Seçim Modalı */}
      <Dialog open={isItemModalOpen} onClose={handleCloseItemModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {tt('Stok Ürünü Seçimi', 'Stock Item Selection')}
            <IconButton onClick={handleCloseItemModal}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                placeholder={tt('Ürün adı, barkod veya stok kodu ile arama yapın...', 'Search by item name, barcode or stock code...')}
                value={itemSearchTerm}
                onChange={handleItemSearch}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
            </Grid>
            {!hideWarehouseUi && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>{tt('Depo', 'Warehouse')}</InputLabel>
                <Select value={selectedWarehouseId || "all"} onChange={handleWarehouseChange} label={tt('Depo', 'Warehouse')}>
                  {!isManager && <MenuItem value="all">{tt('Tüm Depolar', 'All Warehouses')}</MenuItem>}
                  {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            )}
          </Grid>
          
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tt('Ürün Adı', 'Item Name')}</TableCell>
                  <TableCell>{tt('Stok Kodu', 'Stock Code')}</TableCell>
                  <TableCell>{tt('Barkod', 'Barcode')}</TableCell>
                  <TableCell align="right">{tt('Stok Miktarı', 'Stock Quantity')}</TableCell>
                  <TableCell>{tt('Depo', 'Warehouse')}</TableCell>
                  <TableCell align="right">{tt('Alış Fiyatı', 'Purchase Price')}</TableCell>
                  <TableCell>{tt('İşlem', 'Action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      {tt('Ürünler yükleniyor...', 'Loading items...')}
                    </TableCell>
                  </TableRow>
                ) : inventoryItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {itemSearchTerm ? tt('Arama sonucu bulunamadı.', 'No search results found.') : tt('Ürün bulunamadı.', 'No items found.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  inventoryItems.map((item) => {
                    const totalQuantity = item.warehouseStocks?.reduce((sum: number, ws: WarehouseStockDto) => sum + ws.quantity, 0) || 0;
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.stockCode}</TableCell>
                        <TableCell>{item.barcode || '-'}</TableCell>
                        <TableCell align="right">{totalQuantity} {item.unit}</TableCell>
                        <TableCell>
                          {selectedWarehouseId 
                            ? warehouses.find(w => w.id === selectedWarehouseId)?.name || '-'
                            : tt('Tüm Depolar', 'All Warehouses')}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.purchasePrice)}</TableCell>
                        <TableCell>
                          <Button size="small" variant="contained" color="primary" onClick={() => handleAddItem(item)}>
                            {tt('Ekle', 'Add')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalCount}
            page={itemsPage}
            onPageChange={handleItemsPageChange}
            rowsPerPage={itemsRowsPerPage}
            onRowsPerPageChange={handleItemsRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={tt('Sayfa başına satır:', 'Rows per page:')}
            labelDisplayedRows={({ from, to, count }) => (isTr ? `${count} kayıttan ${from}-${to} arası` : `${from}-${to} of ${count}`)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
      </Dialog>
      
      {/* Başarı Modalı */}
      <InvoiceSuccessModal
        open={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        invoiceNumber={invoiceNumber}
        isEditMode={!!editingInvoiceId}
        mode={mode}
        documentSide="purchase"
        lotWarnings={lotWarnings}
      />

      {/* Başka Belgeden Kalem Aktar Modalı */}
      <CopyItemsFromDocumentModal
        open={isCopyFromDocumentModalOpen}
        onClose={() => setIsCopyFromDocumentModalOpen(false)}
        mode={mode}
        initialSide="purchase"
        currentDocumentId={editingInvoiceId || undefined}
        onItemsImport={handleImportItemsFromDocument}
      />
      
      {/* Taslak Modalı */}
      <Dialog open={isDraftModalOpen} onClose={() => { setIsDraftModalOpen(false); setDraftData(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">{tt('Kaydedilmiş Taslak Bulundu', 'Saved Draft Found')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
            <DraftsIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom align="center">
              {tt('Daha önce kaydedilmiş bir taslak var', 'There is a previously saved draft')}
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 1 }}>
              {draftData?.savedAt && `${tt('Son kayıt', 'Last saved')}: ${new Date(draftData.savedAt).toLocaleString(isTr ? 'tr-TR' : 'en-US')}`}
            </Typography>
            {draftData?.selectedAccount && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, width: '100%' }}>
                <Typography variant="caption" color="text.secondary">
                  {tt('Cari', 'Account')}: <strong>{draftData.selectedAccount.companyName}</strong>
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  {tt('Ürün Sayısı', 'Item Count')}: <strong>{draftData.selectedItems?.length || 0}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => { clearDraftFromLocalStorage(); setIsDraftModalOpen(false); setDraftData(null); }}
            variant="outlined" 
            color="error"
            fullWidth
          >
            {tt('Hayır, Yeni Fatura Oluştur', 'No, Create New Invoice')}
          </Button>
          <Button onClick={handleApplyDraft} variant="contained" color="primary" fullWidth startIcon={<HistoryIcon />}>
            {tt('Evet, Taslağı Yükle', 'Yes, Load Draft')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* PDF Upload Modalı */}
      <PdfInvoiceUploadModal
        open={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        onItemsLoaded={handlePdfItemsLoaded}
        companyId={companyId || ''}
      />
      
      {/* E-Fatura Cari Eşleştirme Modalı */}
      {ettnFromUrl && eInvoiceUsername && eInvoicePassword && companyId && (
        <EInvoiceAccountMatchModal
          open={isAccountMatchModalOpen}
          onClose={() => setIsAccountMatchModalOpen(false)}
          onAccountSelected={handleAccountSelected}
          companyId={companyId}
          supplierInfo={eInvoiceSupplierInfo}
        />
      )}
      
      {/* E-Fatura Stok Eşleştirme Modalı */}
      {ettnFromUrl && eInvoiceUsername && eInvoicePassword && selectedAccount && (
        <EInvoiceImportModal
          open={isEInvoiceModalOpen}
          onClose={() => setIsEInvoiceModalOpen(false)}
          onItemsLoaded={handlePdfItemsLoaded}
          companyId={companyId || ''}
          ettn={ettnFromUrl}
          username={eInvoiceUsername}
          password={eInvoicePassword}
          xmlContent={eInvoiceXmlContent}
        />
      )}
      
      {/* Stok İşleme Modal */}
      <Dialog open={isStockProcessModalOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ bgcolor: stockProcessSuccess ? 'success.main' : 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {stockProcessSuccess ? <CheckCircleOutlineIcon /> : <CircularProgress size={24} sx={{ color: 'white' }} />}
            <Typography variant="h6">
              {stockProcessSuccess ? tt('İşlemler Tamamlandı', 'Operations Completed') : tt('Stok ve Cari İşleniyor', 'Stock and Account Processing')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
            {stockProcessSuccess ? (
              <>
                <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>{tt('Tüm işlemler başarıyla tamamlandı!', 'All operations completed successfully!')}</Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  {tt('✅ Stok:', '✅ Stock:')} {stockProcessCurrentItem} {tt('ürün girişi yapıldı', 'items were added')}
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  {tt('✅ Cari: Tedarikçi bakiyesi güncellendi', '✅ Account: Supplier balance updated')}
                </Typography>
                {lotWarnings.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>{lotWarnings.join(' ')}</Alert>
                )}
              </>
            ) : (
              <>
                <CircularProgress size={64} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>{tt('İşlemler kaydediliyor...', 'Saving operations...')}</Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  {tt('📦 Stok:', '📦 Stock:')} {stockProcessTotalItems} {tt('ürün işleniyor', 'items processing')}
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  {tt('💳 Cari: Tedarikçi bakiyesi güncelleniyor', '💳 Account: Supplier balance updating')}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Toplu Depo Seçimi Modal */}
      <Dialog open={isBulkWarehouseModalOpen} onClose={() => { setIsBulkWarehouseModalOpen(false); setBulkWarehouseId(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{tt('Toplu Depo Seçimi', 'Bulk Warehouse Selection')}</Typography>
            <IconButton onClick={() => { setIsBulkWarehouseModalOpen(false); setBulkWarehouseId(''); }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt('Seçtiğiniz depo, faturadaki tüm ürünlere uygulanacaktır', 'Selected warehouse will be applied to all invoice items')}
            {` (${selectedItems.length} ${tt('ürün', 'items')}).`}
          </Alert>
          
          <FormControl fullWidth size="small">
            <InputLabel>{tt('Depo Seçin', 'Select Warehouse')}</InputLabel>
            <Select value={bulkWarehouseId} onChange={(e) => setBulkWarehouseId(e.target.value)} label={tt('Depo Seçin', 'Select Warehouse')}>
              <MenuItem value=""><em>{tt('Depo Seçin', 'Select Warehouse')}</em></MenuItem>
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsBulkWarehouseModalOpen(false); setBulkWarehouseId(''); }}>{tt('İptal', 'Cancel')}</Button>
          <Button onClick={handleBulkWarehouseSelect} variant="contained" color="primary" disabled={!bulkWarehouseId} startIcon={<WarehouseIcon />}>
            {tt('Uygula', 'Apply')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ✅ SİPARİŞ STOK ONAY DİALOGU - Alış Siparişi */}
      <Dialog open={showStockConfirmDialog} onClose={() => setShowStockConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{tt('Stok İşleme Onayı', 'Stock Processing Confirmation')}</Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              {tt('Sipariş kaydedilirken stoğa kaydedilsin mi?', 'Should items be added to stock while saving the order?')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {tt('Bu işlem geri alınamaz. Sipariş kaydedildiğinde seçilen ürünler stoğa eklenecektir.', 'This action cannot be undone. Selected items will be added to stock when the order is saved.')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => { setShowStockConfirmDialog(false); handleSaveOrder(false); }} 
            variant="outlined" color="primary" fullWidth
          >
            {tt('Hayır, Stoğa Kaydedilmesin', 'No, Do Not Add to Stock')}
          </Button>
          <Button 
            onClick={() => { setShowStockConfirmDialog(false); handleSaveOrder(true); }} 
            variant="contained" color="warning" fullWidth
          >
            {tt('Evet, Stoğa Kaydedilsin', 'Yes, Add to Stock')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Miktarı 0 olan kalemler için onay diyaloğu */}
      <Dialog open={showZeroQtyConfirmDialog} onClose={() => setShowZeroQtyConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{tt('Sıfır Miktar Uyarısı', 'Zero Quantity Warning')}</Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {tt('Aşağıdaki kalem(ler)in miktarı 0:', 'The following item(s) have a quantity of 0:')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            {zeroQtyItemNames.map((name, i) => (
              <li key={i}><Typography variant="body2">{name}</Typography></li>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {tt('Yine de bu teklifi kaydetmek istiyor musunuz?', 'Do you still want to save this offer?')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setShowZeroQtyConfirmDialog(false)}
            variant="outlined" color="primary" fullWidth
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            onClick={() => { setShowZeroQtyConfirmDialog(false); handleSaveOffer(true); }}
            variant="contained" color="warning" fullWidth
          >
            {tt('Evet, Kaydet', 'Yes, Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseInvoice;
